'use client';

import { useState, useEffect, useMemo, useRef, useContext, lazy, Suspense } from 'react';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useS3FileCache } from '@/hooks/useS3FileCache';
import { AdminSidebarContext } from '@/contexts/AdminSidebarContext';
import { useVideoModal } from '@/contexts/VideoModalContext';
import { mediaCache } from '@/lib/media-cache';
import { getVideoThumbnailUrl } from '@/lib/media/video-thumbnail';
import { useResponsiveVideo } from '@/hooks/useResponsiveVideo';
import { ResponsiveBackgroundImage } from '@/types/sections';
import {
  MediaPriority,
  getPreloadStrategyForPriority,
  shouldPriorityLoad,
  MEDIA_CACHE_VERSION
} from '@/config/media-config';
import { videoPosterCache } from '@/lib/video-poster-cache';
import { getMediaTypeFromUrl } from '@/lib/video-utils';
import { isMobileUserAgent } from '@/lib/utils/mobile-detection';
import { isSlowConnection } from '@/lib/network-utils';
import { isVideoAlreadyHandled, markVideoLoading, markVideoComplete, globalVideoLoadTracker } from '@/lib/video-load-deduplication';
// Lazy load icons to reduce initial bundle size
const Play = lazy(() => import('lucide-react').then(mod => ({ default: mod.Play })));
const Camera = lazy(() => import('lucide-react').then(mod => ({ default: mod.Camera })));

// Import extracted utilities
import { getFileTypeFromUrl, categorizeFileType, extractFileTypeFromUrl } from '@/lib/file-type-utils';
import { normalizeMediaUrl, extractS3KeyFromUrl, getCanonicalMediaUrl } from '@/lib/url-normalization';
import { FileIconRenderer } from './S3FilePreview/FileIcon';
import { SkeletonLoader } from './S3FilePreview/SkeletonLoader';

interface S3FilePreviewProps {
  src: string | ResponsiveBackgroundImage;
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
  fileType?: string;
  showDownload?: boolean;
  fileName?: string;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onLoad?: () => void;
  onError?: (error?: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  onDimensionsLoaded?: (dimensions: { width: number; height: number }) => void;
  disablePreviewModal?: boolean; // Defaults to true - set to false to enable preview modal
  modalClassName?: string;
  disableSkeleton?: boolean;
  skeletonStyle?: 'default' | 'admin'; // 'default' for gray shimmer, 'admin' for black with spinner
  // Force use of regular img tag instead of Next.js Image
  forceImgTag?: boolean;
  // Video-specific props
  autoplay?: boolean;
  loop?: boolean;
  muted?: boolean;
  controls?: boolean;
  playsInline?: boolean;
  preload?: 'none' | 'metadata' | 'auto';
  /**
   * Priority loading - controls preload strategy and lazy loading behavior
   * - boolean: true = high priority (preload=metadata + load()), false = normal (preload=metadata)
   * - MediaPriority enum: CRITICAL/HIGH/NORMAL/LOW/BACKGROUND for granular control
   *   - CRITICAL: preload=metadata + immediate load(), fetchpriority=high (hero videos)
   *   - HIGH: preload=metadata, eager load (above fold)
   *   - NORMAL: preload=none, lazy load (mid-page)
   *   - LOW/BACKGROUND: preload=none, lazy load + pause when not visible
   */
  priority?: boolean | MediaPriority;
  // Video poster image URL (thumbnail/preview image)
  // Supports both string (single image) and ResponsiveBackgroundImage (mobile/desktop variants)
  poster?: string | ResponsiveBackgroundImage;
  // Callback to expose video element ref to parent components
  videoRefCallback?: (ref: HTMLVideoElement | null) => void;
}

// Utility functions moved to @/lib/file-type-utils.ts for better code splitting

// Global map to track video loads across all component instances (for load() call deduplication)
// Key: normalized URL, Value: timestamp when load was initiated
const videoLoadTracker = new Map<string, number>();

// Maximum time a video load entry should stay in the tracker.
// This matches the "recentlyLoaded" window used to guard load() calls.
const VIDEO_LOAD_EXPIRY_MS = 100;
const videoPreviewDebugEnabled =
  process.env.NODE_ENV !== 'production' ||
  process.env.NEXT_PUBLIC_VIDEO_PREVIEW_DEBUG === 'true';

const logVideoPreviewDebug = (message: string, data?: Record<string, unknown>) => {
  if (!videoPreviewDebugEnabled || typeof window === 'undefined') return;
  console.log('[VideoPreviewDebug]', message, data ?? {});
};

const shouldRetryWithNoCdn = (status: number): boolean => {
  // CloudFront misconfig / permissions commonly return 403.
  // 5xx can also happen transiently in CDN path.
  return status === 403 || status >= 500;
};

// Prune stale entries from the global tracker to avoid unbounded growth
const pruneVideoLoadTracker = (now: number = Date.now()) => {
  if (!videoLoadTracker.size) return;
  for (const [key, timestamp] of videoLoadTracker) {
    if (now - timestamp > VIDEO_LOAD_EXPIRY_MS) {
      videoLoadTracker.delete(key);
    }
  }
};

// Helper to record that a video load has been initiated, with pruning
const markVideoLoadInitiated = (normalizedUrl: string, now: number = Date.now()) => {
  pruneVideoLoadTracker(now);
  videoLoadTracker.set(normalizedUrl, now);
};

// extractFileTypeFromUrl moved to @/lib/file-type-utils.ts

/**
 * Componente optimizado para mostrar previsualizaciones de archivos de S3
 * Usa caché global para evitar múltiples llamadas a la API
 */
// Helper to play video using requestIdleCallback for better performance (reduces TBT)
// On mobile, calls play() immediately (autoplay requires immediate execution)
// On desktop, defers playback until idle time to reduce main thread blocking
const playVideoIdle = (video: HTMLVideoElement, onError?: (error: Error) => void, isMobile?: boolean): void => {
  // Mobile: call play() immediately - autoplay policies require immediate execution
  // Desktop: defer with requestIdleCallback to reduce TBT
  const shouldPlayImmediately = isMobile;

  if (shouldPlayImmediately) {
    // Mobile: play immediately (required for autoplay to work)
    if (video && !video.paused) return; // Already playing
    video.play().catch((error) => {
      if (onError) onError(error);
    });
  } else if ('requestIdleCallback' in window) {
    // Desktop: defer with requestIdleCallback
    requestIdleCallback(() => {
      if (video && !video.paused) return; // Already playing
      video.play().catch((error) => {
        if (onError) onError(error);
      });
    }, { timeout: 1000 }); // Max 1 second wait
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(() => {
      if (video && !video.paused) return; // Already playing
      video.play().catch((error) => {
        if (onError) onError(error);
      });
    }, 100);
  }
};

const S3FilePreview = ({
  src,
  alt = 'File preview',
  className = '',
  style,
  fileType: providedFileType,
  fileName,
  onClick,
  onLoad,
  onError,
  onDimensionsLoaded,
  disablePreviewModal = true, // Disabled by default - set to false to enable preview modal
  modalClassName,
  disableSkeleton = false,
  skeletonStyle = 'default',
  forceImgTag = false,
  // Video props with defaults
  autoplay = false,
  loop = false,
  muted = false,
  controls = true,
  playsInline = false,
  preload = 'metadata',
  priority = false,
  poster,
  videoRefCallback,
}: S3FilePreviewProps) => {
  const [imageError, setImageError] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [fileNotFound, setFileNotFound] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [videoReadyState, setVideoReadyState] = useState(0); // Track video readyState
  const [loadStartTime, setLoadStartTime] = useState<number>(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  // Track the src we've set programmatically to prevent React from resetting it
  const programmaticSrcRef = useRef<string | null>(null);
  
  // Track when poster image first appears and when video is ready
  const posterImageStartTime = useRef<number | null>(null);
  const [videoReadyForPlayback, setVideoReadyForPlayback] = useState(false);
  const [canHidePoster, setCanHidePoster] = useState(false);

  // Use video modal context for videos
  const { openModal: openVideoModal } = useVideoModal();
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [detectedContentType, setDetectedContentType] = useState<string | null>(null);
  const [generatedPosterUrl, setGeneratedPosterUrl] = useState<string | null>(null);
  const [serverThumbnailUrl, setServerThumbnailUrl] = useState<string | null>(null);
  const [serverThumbChecked, setServerThumbChecked] = useState(false);
  // Hidden video element for early frame extraction (before main video loads)
  const hiddenVideoRef = useRef<HTMLVideoElement | null>(null);
  const isExtractingFrame = useRef(false);
  
  // Check if connection is slow enough to show WebP fallback instead of video
  const [isSlow, setIsSlow] = useState(false);
  
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsSlow(isSlowConnection());
    }
  }, []);

  // CRITICAL: Track if page is fully loaded (for CRITICAL priority videos)
  // Videos should only show after both video AND page are fully loaded
  const [isPageFullyLoaded, setIsPageFullyLoaded] = useState(false);
  const [isVideoFullyLoaded, setIsVideoFullyLoaded] = useState(false);
  
  // CORS retry: If CloudFront fails due to CORS, retry with direct S3 (?nocdn=true)
  const [corsRetryAttempted, setCorsRetryAttempted] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Check if page is already loaded
    if (document.readyState === 'complete') {
      setIsPageFullyLoaded(true);
      return;
    }

    // Listen for page load event
    const handleLoad = () => {
      setIsPageFullyLoaded(true);
    };

    window.addEventListener('load', handleLoad, { once: true });

    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, []);
  const [isMobile, setIsMobile] = useState(false);
  
  // Resolve responsive poster/fallback image to mobile/desktop version
  const resolvedPoster = useMemo(() => {
    if (!poster) return undefined;
    
    // If it's a string, return as-is
    if (typeof poster === 'string') {
      return poster;
    }
    
    // If it's a responsive object, resolve based on device
    if (typeof poster === 'object' && poster !== null) {
      // Use synchronous mobile detection for immediate resolution
      const isMobileSync = isMobileUserAgent();
      
      if (isMobileSync) {
        // On mobile, prefer mobile version, fallback to desktop if mobile doesn't exist
        return poster.mobile || poster.desktop;
      } else {
        // On desktop, prefer desktop version, fallback to mobile if desktop doesn't exist
        return poster.desktop || poster.mobile;
      }
    }
    
    return undefined;
  }, [poster]);

  
  // Mobile detection - use user agent only to distinguish actual mobile devices from desktop
  // This prevents small desktop windows from being treated as mobile
  useEffect(() => {
    const checkMobile = () => {
      // Only use user agent detection for actual mobile device detection
      // Desktop devices (even with small windows) should autoplay
      const newIsMobile = isMobileUserAgent();
      setIsMobile(newIsMobile);
    };

    checkMobile();
    // No resize listener needed - mobile detection is based on device type, not viewport
  }, []);

  // On mobile, for background videos (autoplay=true, controls=false), disable autoplay initially
  // and wait for section tap. On desktop, keep autoplay enabled.
  const effectiveAutoplay = useMemo(() => {
    if (!autoplay) return false;
    // On mobile, if it's a background video (controls=false), disable autoplay initially (wait for tap)
    // On desktop, keep autoplay enabled - ensure desktop always autoplays
    if (isMobile && !controls) {
      return false; // Will be enabled on section tap
    }
    // Desktop should always autoplay (unless explicitly disabled)
    // Force autoplay for desktop devices
    return true;
  }, [autoplay, controls, isMobile]);
  const [shouldAutoplay, setShouldAutoplay] = useState(effectiveAutoplay);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  // Track if we've already tried to play from section tap (for mobile tap-to-play)
  const sectionTapPlayAttemptedRef = useRef(false);
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);
  const [computedObjectFit, setComputedObjectFit] = useState<'contain' | 'cover' | 'fill' | 'none' | 'scale-down'>('cover');

  // Update shouldAutoplay when effectiveAutoplay changes
  useEffect(() => {
    setShouldAutoplay(effectiveAutoplay);
    // Reset section tap attempt ref when autoplay changes
    sectionTapPlayAttemptedRef.current = false;
  }, [effectiveAutoplay]);
  
  const videoContainerRef = useRef<HTMLDivElement>(null);

  // Get sidebar state to adjust modal positioning (safe access for shared component)
  const pathname = usePathname();
  const isLiveEditorRoute = pathname?.includes('/live-editor');
  const isAdminRoute = pathname?.startsWith('/admin');
  // Safely access AdminSidebarContext - may be undefined if not within provider
  const adminSidebarContext = useContext(AdminSidebarContext);

  // Handle responsive video URLs - get the appropriate URL based on device
  const responsiveVideoUrl = useResponsiveVideo(
    typeof src === 'string' ? src : (src as ResponsiveBackgroundImage)
  );
  
  // Use the resolved URL (either the string src or the responsive video URL)
  const effectiveSrc = typeof src === 'string' ? src : (responsiveVideoUrl || src.desktop || src.mobile || '');
  
  // Use the S3 file cache hook with error handling
  const s3CacheResult = useS3FileCache(effectiveSrc || '');
  const { finalUrl, isS3Url, s3Key } = s3CacheResult;

  // Add safety checks for the hook results
  const safeFinalUrl = finalUrl || effectiveSrc;
  const safeIsS3Url = Boolean(isS3Url);
  const safeS3Key = s3Key || null;

  // Normalize to canonical URL (without version params) for deduplication
  // This prevents duplicate downloads when the same file is loaded multiple times
  // If CORS retry was attempted, add nocdn=true to bypass CloudFront
  const normalizedFinalUrl = useMemo(() => {
    if (!safeFinalUrl) return null;
    let url = getCanonicalMediaUrl(safeFinalUrl) ?? null;
    // Add nocdn=true to bypass CloudFront if CORS retry was attempted
    if (url && corsRetryAttempted && url.includes('/api/media/download')) {
      const separator = url.includes('?') ? '&' : '?';
      url = `${url}${separator}nocdn=true`;
    }
    return url;
  }, [safeFinalUrl, corsRetryAttempted]);

  // CDN-first behavior: reset direct-S3 fallback when the media source changes.
  // This allows new videos to try CloudFront again instead of staying stuck on nocdn=true.
  const lastMediaUrlRef = useRef<string | null>(null);
  useEffect(() => {
    const currentBaseMediaUrl = getCanonicalMediaUrl(safeFinalUrl) ?? safeFinalUrl ?? null;
    if (lastMediaUrlRef.current !== currentBaseMediaUrl) {
      lastMediaUrlRef.current = currentBaseMediaUrl;
      setCorsRetryAttempted(false);
    }
  }, [safeFinalUrl]);

  // Reset section tap attempt ref when video source changes
  useEffect(() => {
    sectionTapPlayAttemptedRef.current = false;
  }, [normalizedFinalUrl]);

  // Note: Using global videoLoadTracker Map instead of per-component ref
  // to prevent race conditions when multiple instances load the same video

  // Memoize the file analysis to avoid recalculation on every render
  const fileAnalysis = useMemo(() => {
    if (!effectiveSrc) return null;

    // Normalize and extract S3 key from URL
    const normalizedSrc = typeof effectiveSrc === 'string' ? normalizeMediaUrl(effectiveSrc) || effectiveSrc : effectiveSrc;
    const fileKey = typeof normalizedSrc === 'string' ? extractS3KeyFromUrl(normalizedSrc) : effectiveSrc;

    // Determinar el tipo de archivo - check both original effectiveSrc and extracted key
    const queryFileType =
      extractFileTypeFromUrl(normalizedSrc) ||
      extractFileTypeFromUrl(effectiveSrc) ||
      null;

    let detectedFileType =
      providedFileType ||
      queryFileType ||
      getFileTypeFromUrl(fileKey) ||
      getFileTypeFromUrl(normalizedSrc) ||
      getFileTypeFromUrl(effectiveSrc);

    // More robust file type detection based on URL patterns - check both src and fileKey
    const urlLower = normalizedSrc.toLowerCase();
    const keyLower = fileKey.toLowerCase();

    // Check for known image hosting services (Unsplash, etc.) - assume images
    const isKnownImageHost = urlLower.includes('unsplash.com') ||
      urlLower.includes('images.unsplash.com') ||
      urlLower.includes('pexels.com') ||
      urlLower.includes('pixabay.com') ||
      (urlLower.includes('photo-') && (urlLower.includes('?w=') || urlLower.includes('&w=')));

    // Override detection for common image formats - check both URL and key
    if (urlLower.includes('.png') || urlLower.includes('png') || keyLower.includes('.png') || keyLower.includes('png')) {
      detectedFileType = 'image/png';
    } else if (urlLower.includes('.jpg') || urlLower.includes('.jpeg') || urlLower.includes('jpg') || urlLower.includes('jpeg') ||
      keyLower.includes('.jpg') || keyLower.includes('.jpeg') || keyLower.includes('jpg') || keyLower.includes('jpeg')) {
      detectedFileType = 'image/jpeg';
    } else if (urlLower.includes('.gif') || urlLower.includes('gif') || keyLower.includes('.gif') || keyLower.includes('gif')) {
      detectedFileType = 'image/gif';
    } else if (urlLower.includes('.webp') || urlLower.includes('webp') || keyLower.includes('.webp') || keyLower.includes('webp')) {
      detectedFileType = 'image/webp';
    } else if (urlLower.includes('.svg') || urlLower.includes('svg') || keyLower.includes('.svg') || keyLower.includes('svg')) {
      detectedFileType = 'image/svg+xml';
    } else if (urlLower.includes('.pdf') || urlLower.includes('pdf') || keyLower.includes('.pdf') || keyLower.includes('pdf')) {
      detectedFileType = 'application/pdf';
    } else if (urlLower.includes('.mp4') || urlLower.includes('mp4') || keyLower.includes('.mp4') || keyLower.includes('mp4')) {
      detectedFileType = 'video/mp4';
    } else if (urlLower.includes('.webm') || urlLower.includes('webm') || keyLower.includes('.webm') || keyLower.includes('webm')) {
      detectedFileType = 'video/webm';
    } else if (urlLower.includes('.mov') || urlLower.includes('mov') || keyLower.includes('.mov') || keyLower.includes('mov')) {
      detectedFileType = 'video/quicktime';
    } else if (isKnownImageHost) {
      // Assume images from known image hosting services
      detectedFileType = 'image/jpeg';
    } else if (safeIsS3Url && safeS3Key && !detectedFileType.includes('pdf') && !detectedFileType.includes('video')) {
      // For S3 URLs without clear extension, check if it's likely an image
      // Common image patterns in S3 keys
      if (keyLower.includes('photo') || keyLower.includes('image') || keyLower.includes('img') ||
        keyLower.includes('picture') || keyLower.match(/^[a-z0-9-]+$/)) {
        // If it looks like an image key (alphanumeric with dashes, no extension), assume JPEG
        detectedFileType = 'image/jpeg';
      }
    }

    // Fallback: If we still don't have a clear type and it's from S3/API, default to image for unknown types
    // This handles cases where the key doesn't have an extension but is actually an image
    if (detectedFileType === 'application/octet-stream' && safeIsS3Url && safeS3Key) {
      // Check if it's definitely NOT a video or PDF
      const definitelyNotImage = keyLower.includes('.pdf') ||
        keyLower.includes('.mp4') ||
        keyLower.includes('.webm') ||
        keyLower.includes('.mov') ||
        keyLower.includes('video');
      if (!definitelyNotImage) {
        // Default to image for unknown S3 files (most S3 files in this context are images)
        detectedFileType = 'image/jpeg';
      }
    }

    // Fallback by filename extension when content type is generic or unreliable
    if (fileName) {
      const fileNameLower = fileName.toLowerCase();
      if ((detectedFileType === 'application/octet-stream' || !detectedFileType) && (
        fileNameLower.endsWith('.jpg') ||
        fileNameLower.endsWith('.jpeg')
      )) {
        detectedFileType = 'image/jpeg';
      } else if ((detectedFileType === 'application/octet-stream' || !detectedFileType) && fileNameLower.endsWith('.png')) {
        detectedFileType = 'image/png';
      } else if ((detectedFileType === 'application/octet-stream' || !detectedFileType) && fileNameLower.endsWith('.webp')) {
        detectedFileType = 'image/webp';
      } else if ((detectedFileType === 'application/octet-stream' || !detectedFileType) && fileNameLower.endsWith('.gif')) {
        detectedFileType = 'image/gif';
      } else if ((detectedFileType === 'application/octet-stream' || !detectedFileType) && fileNameLower.endsWith('.svg')) {
        detectedFileType = 'image/svg+xml';
      } else if (!detectedFileType.includes('pdf') && fileNameLower.endsWith('.pdf')) {
        detectedFileType = 'application/pdf';
      }
    }

    // Use detected content type from API response if available
    if (detectedContentType && detectedContentType.startsWith('image/')) {
      detectedFileType = detectedContentType;
    } else if (detectedContentType && detectedContentType.startsWith('video/')) {
      detectedFileType = detectedContentType;
    }

    // Actualizar estados derivados del tipo de archivo
    const isImage = detectedFileType.startsWith('image/');
    const isPdf = detectedFileType === 'application/pdf';
    const isVideo = detectedFileType.startsWith('video/');
    const isSvg = detectedFileType === 'image/svg+xml' ||
      urlLower.includes('.svg') || urlLower.includes('svg');
    // #region agent log
    if (typeof window !== 'undefined') {
      const logData = {location:'S3FilePreview.tsx:298',message:'File type detection',data:{effectiveSrc,providedFileType,fileName,detectedFileType,isVideo,isImage,normalizedSrc,fileKey,safeIsS3Url,safeS3Key,urlLower,keyLower},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'};
      console.log('[DEBUG]', logData);
    }
    // #endregion

    // Determinar la categoría del archivo
    const fileCategory = categorizeFileType(detectedFileType);

      return {
      detectedFileType,
      isImage,
      isPdf,
      isVideo,
      isSvg,
      fileCategory
    };
  }, [effectiveSrc, providedFileType, fileName, detectedContentType, safeIsS3Url, safeS3Key]);

  // Track normalizedFinalUrl changes and check for duplicates
  const prevNormalizedUrlRef = useRef<string | null>(null);
  const shouldSkipVideoSrc = useRef<boolean>(false);
  
  useEffect(() => {
    if (prevNormalizedUrlRef.current !== normalizedFinalUrl) {
      
      // Check if this video is already being handled by another component
      if (normalizedFinalUrl && fileAnalysis?.isVideo) {
        const alreadyHandled = isVideoAlreadyHandled(normalizedFinalUrl);
        shouldSkipVideoSrc.current = alreadyHandled;
      } else {
        shouldSkipVideoSrc.current = false;
      }
      
      prevNormalizedUrlRef.current = normalizedFinalUrl;
      // Initialize programmaticSrcRef when normalizedFinalUrl first becomes available
      if (normalizedFinalUrl && !programmaticSrcRef.current) {
        programmaticSrcRef.current = normalizedFinalUrl;
      } else if (normalizedFinalUrl && programmaticSrcRef.current !== normalizedFinalUrl) {
        // Keep programmatic src aligned with latest resolved URL (e.g. nocdn retry)
        programmaticSrcRef.current = normalizedFinalUrl;
      }
    }
  }, [normalizedFinalUrl, fileAnalysis?.isVideo]);

  // For videos with boolean priority=true, treat as CRITICAL to ensure hero videos load with highest priority
  // This ensures consistent behavior: boolean true for videos = CRITICAL priority
  const effectivePriority: boolean | MediaPriority = 
    (fileAnalysis?.isVideo && typeof priority === 'boolean' && priority === true)
      ? 'CRITICAL'
      : (typeof priority === 'string' && priority === 'CRITICAL') ? 'CRITICAL' : priority;
  // #region agent log
  if (typeof window !== 'undefined' && fileAnalysis?.isVideo) {
    const logData = {location:'S3FilePreview.tsx:343',message:'Video priority processing',data:{priority,effectivePriority,isVideo:fileAnalysis.isVideo,effectiveSrc},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'};
    console.log('[DEBUG]', logData);
  }
  // #endregion

  // Compute preload strategy and priority loading from effective priority
  // This supports both legacy boolean and new MediaPriority enum
  const computedPreloadStrategy = getPreloadStrategyForPriority(effectivePriority);
  const isPriorityLoad = shouldPriorityLoad(effectivePriority);

  // Track when video is fully loaded (readyState >= 4 = HAVE_ENOUGH_DATA)
  // Moved here after fileAnalysis, isPriorityLoad, and normalizedFinalUrl are declared
  useEffect(() => {
    if (!videoRef.current || !fileAnalysis?.isVideo || !isPriorityLoad) return;

    const video = videoRef.current;

    const checkVideoReady = () => {
      if (video.readyState >= 4) {
        setIsVideoFullyLoaded(true);
      }
    };

    // Check immediately if already loaded
    checkVideoReady();

    // Listen for video ready events
    const events = ['canplaythrough', 'loadeddata', 'loadedmetadata'] as const;
    events.forEach(event => {
      video.addEventListener(event, checkVideoReady, { once: true });
    });

    return () => {
      events.forEach(event => {
        video.removeEventListener(event, checkVideoReady);
      });
    };
  }, [fileAnalysis?.isVideo, isPriorityLoad, normalizedFinalUrl]);

  // For CRITICAL videos, show video when loaded OR when it has started playing
  // This ensures video becomes visible as soon as playback begins
  const shouldShowVideo = isPriorityLoad 
    ? (isPageFullyLoaded && isVideoFullyLoaded) || hasPlayed
    : !isSlow; // For non-CRITICAL videos, use old isSlow logic

  // Determine effective preload strategy:
  // - If MediaPriority enum is used (string), computed strategy takes precedence
  // - If legacy boolean priority is used, respect explicit preload prop
  // - This ensures LOW/NORMAL/BACKGROUND videos don't preload aggressively
  const isEnumPriority = typeof effectivePriority === 'string';
  const effectivePreload = isEnumPriority
    ? computedPreloadStrategy  // Enum priority: always use computed
    : (preload !== 'metadata' ? preload : computedPreloadStrategy); // Boolean: respect explicit preload

  // Check for server-side generated thumbnail for videos
  useEffect(() => {
    if (!fileAnalysis?.isVideo || !safeS3Key || poster || serverThumbChecked) return;

    const checkServerThumbnail = async () => {
      try {
        const thumbUrl = getVideoThumbnailUrl(safeS3Key);
        const response = await fetch(thumbUrl, { method: 'HEAD' });
        if (response.ok) {
          setServerThumbnailUrl(thumbUrl);
        }
      } catch (error) {
        console.warn('[S3FilePreview] Failed to check server thumbnail:', error);
      } finally {
        setServerThumbChecked(true);
      }
    };

    checkServerThumbnail();
  }, [fileAnalysis?.isVideo, safeS3Key, poster, serverThumbChecked]);

  // State to track if video src should be set (to prevent duplicate loads)
  // For images, start with true since they handle loading differently
  // For videos, start with false and check cache before setting
  const [shouldSetVideoSrc, setShouldSetVideoSrc] = useState(true);

  useEffect(() => {
    if (!fileAnalysis?.isVideo) return;
    logVideoPreviewDebug('video-state-snapshot', {
      src: effectiveSrc,
      normalizedFinalUrl,
      shouldSetVideoSrc,
      programmaticSrc: programmaticSrcRef.current,
      controls,
      shouldAutoplay,
      effectivePreload,
      isPriorityLoad,
      isPageFullyLoaded,
      isVideoFullyLoaded,
      shouldShowVideo,
      isLoading,
      videoError,
      fileNotFound,
      autoplayBlocked,
      hasPlayed,
      hasPoster: !!generatedPosterUrl || !!resolvedPoster || !!serverThumbnailUrl,
      videoReadyState,
      corsRetryAttempted,
    });
  }, [
    fileAnalysis?.isVideo,
    effectiveSrc,
    normalizedFinalUrl,
    shouldSetVideoSrc,
    controls,
    shouldAutoplay,
    effectivePreload,
    isPriorityLoad,
    isPageFullyLoaded,
    isVideoFullyLoaded,
    shouldShowVideo,
    isLoading,
    videoError,
    fileNotFound,
    autoplayBlocked,
    hasPlayed,
    generatedPosterUrl,
    resolvedPoster,
    serverThumbnailUrl,
    videoReadyState,
    corsRetryAttempted,
  ]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !fileAnalysis?.isVideo) return;

    const eventNames = [
      'loadstart',
      'loadedmetadata',
      'loadeddata',
      'canplay',
      'canplaythrough',
      'progress',
      'playing',
      'pause',
      'waiting',
      'stalled',
      'suspend',
      'emptied',
      'seeking',
      'seeked',
      'error',
    ] as const;

    const logEvent = (eventName: string) => {
      const mediaError = video.error
        ? {
            code: video.error.code,
            message: (video.error as MediaError & { message?: string }).message ?? '',
          }
        : null;

      logVideoPreviewDebug(`event:${eventName}`, {
        currentSrc: video.currentSrc,
        srcAttr: video.getAttribute('src'),
        networkState: video.networkState,
        readyState: video.readyState,
        paused: video.paused,
        currentTime: video.currentTime,
        duration: Number.isFinite(video.duration) ? video.duration : null,
        bufferedRanges: video.buffered.length,
        poster: video.poster,
        mediaError,
      });
    };

    const handlers = eventNames.map((eventName) => {
      const handler = () => logEvent(eventName);
      video.addEventListener(eventName, handler);
      return { eventName, handler };
    });

    logVideoPreviewDebug('attached-video-debug-listeners', {
      src: effectiveSrc,
      normalizedFinalUrl,
      controls,
      shouldAutoplay,
      effectivePreload,
    });

    return () => {
      handlers.forEach(({ eventName, handler }) => {
        video.removeEventListener(eventName, handler);
      });
      logVideoPreviewDebug('detached-video-debug-listeners', { normalizedFinalUrl });
    };
  }, [
    fileAnalysis?.isVideo,
    normalizedFinalUrl,
    effectiveSrc,
    controls,
    shouldAutoplay,
    effectivePreload,
  ]);

  // Consolidated cache check and loading state management
  // Single useEffect to avoid redundant cache checks and race conditions
  useEffect(() => {
    // Reset error states on src change
    setImageError(false);
    setVideoError(false);
    setFileNotFound(false);

    if (!normalizedFinalUrl) {
      setIsLoading(true);
      setLoadStartTime(Date.now());
      setShouldSetVideoSrc(false);
      return;
    }

    // Track if this effect is still active
    let isActive = true;

    // Quick synchronous check first (fastest path)
    if (mediaCache.isPreloaded(normalizedFinalUrl)) {
      setIsLoading(false);
      setShouldSetVideoSrc(true); // Safe to set src if already preloaded
      if (shouldLoadImmediately) setIsInView(true);
      // For videos, mark in tracker that this URL is already loaded
      if (fileAnalysis?.isVideo) {
        const now = Date.now();
        markVideoLoadInitiated(normalizedFinalUrl, now);
      }
      return;
    }

    // For videos, check if another component is already loading this URL
    if (fileAnalysis?.isVideo && normalizedFinalUrl) {
      const now = Date.now();
      pruneVideoLoadTracker(now);
      const lastLoadTime = videoLoadTracker.get(normalizedFinalUrl);
      const recentlyLoaded = lastLoadTime !== undefined && (now - lastLoadTime) < 5000; // 5 second window
      
      if (recentlyLoaded) {
        // Another component is loading this video - wait a bit before setting src
        // This prevents multiple video elements from loading the same video simultaneously
        setShouldSetVideoSrc(false);
        // Check again after a short delay
        setTimeout(() => {
          setShouldSetVideoSrc(true);
        }, 500);
      }
    }

    // Set loading state and start time
    setLoadStartTime(Date.now());

    // Async cache check
    const checkCacheAsync = async () => {
      if (!isActive) return;

      // Check browser cache
      const isCached = await mediaCache.isInBrowserCache(normalizedFinalUrl);

      if (!isActive) return;

      if (isCached) {
        setIsLoading(false);
        setShouldSetVideoSrc(true); // Safe to set src if cached
      } else {
        setIsLoading(true);
        // For videos, check if another component is already loading this URL
        if (fileAnalysis?.isVideo) {
          const now = Date.now();
          pruneVideoLoadTracker(now);
          const lastLoadTime = videoLoadTracker.get(normalizedFinalUrl);
          const recentlyLoaded = lastLoadTime !== undefined && (now - lastLoadTime) < 5000; // 5 second window
          
          // Only set src if not recently loaded by another component
          setShouldSetVideoSrc(!recentlyLoaded);
        } else {
          setShouldSetVideoSrc(true);
        }
      }

      // Ensure priority/autoplay content loads immediately (only if high priority)
      if (shouldLoadImmediately) {
        setIsInView(true);
      }
    };

    checkCacheAsync();

    // Fallback timeout: clear loading state after 15 seconds (reduced from 30s)
    const timeoutId = setTimeout(() => {
      if (isActive) {
        setIsLoading(false);
        setShouldSetVideoSrc(true); // Allow src to be set after timeout
        if (process.env.NODE_ENV === 'development') {
          console.warn('[S3FilePreview] Loading timeout after 15s for:', effectiveSrc);
        }
      }
    }, 15000);

    return () => {
      isActive = false;
      clearTimeout(timeoutId);
    };
  }, [effectiveSrc, normalizedFinalUrl, priority, autoplay, fileAnalysis?.isVideo]);

  // State for Intersection Observer (lazy loading)
  // Only load immediately if CRITICAL/HIGH priority - even autoplay videos should lazy load if lower priority
  // This prevents network congestion from too many concurrent video loads
  // In admin/preview routes, always load immediately to ensure previews work correctly
  const shouldLoadImmediately = isPriorityLoad || (autoplay && !isEnumPriority) || isAdminRoute || isLiveEditorRoute;
  const [isInView, setIsInView] = useState(shouldLoadImmediately);
  const containerRef = useRef<HTMLDivElement>(null);

  // Intersection Observer for lazy loading videos and images
  useEffect(() => {
    // Skip lazy loading only for high priority content or admin routes
    // For enum priorities (NORMAL, LOW, BACKGROUND), always use lazy loading even with autoplay
    // In admin routes, always load immediately to ensure previews work
    if (shouldLoadImmediately) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            // Disconnect once loaded
            observer.disconnect();
          }
        });
      },
      {
        // OPTIMIZED: Larger rootMargin for CRITICAL/HIGH priority videos (hero sections)
        rootMargin: isPriorityLoad ? '200px' : '50px',
      }
    );

    const currentRef = containerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
      observer.disconnect();
    };
  }, [priority, autoplay, fileAnalysis?.isVideo, shouldLoadImmediately, isAdminRoute, isLiveEditorRoute]);

  // Video visibility observer - pause when out of viewport, play when in view
  // This saves bandwidth and improves performance by not playing hidden videos
  useEffect(() => {
    const video = videoRef.current;
    // Only apply to videos with autoplay enabled
    if (!video || !fileAnalysis?.isVideo || !autoplay) return;

    const visibilityObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!video) return;

          if (entry.isIntersecting) {
            // Video is visible - try to play
            video.muted = true; // Ensure muted for autoplay
            video.play().catch(() => {
              // Silently handle autoplay errors
            });
          } else {
            // Video is not visible - pause to save bandwidth
            if (!video.paused) {
              video.pause();
            }
          }
        });
      },
      {
        threshold: 0.5, // Trigger when 50% of video is visible
        rootMargin: '0px', // No margin for visibility-based control
      }
    );

    visibilityObserver.observe(video);

    return () => {
      visibilityObserver.disconnect();
    };
  }, [fileAnalysis?.isVideo, autoplay, normalizedFinalUrl]);

  // Don't fetch video as blob URL - let browser handle streaming natively
  // This prevents duplicate requests and allows efficient range requests (206 responses)
  // The browser's native video element handles streaming much better than fetching the entire file
  // No useEffect needed - we just use the URL directly in the video element

  // Track when src attribute changes in DOM (React might be updating it)
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !fileAnalysis?.isVideo) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'src') {
          const currentSrc = video.getAttribute('src');
        }
      });
    });

    observer.observe(video, { attributes: true, attributeFilter: ['src'] });

    return () => observer.disconnect();
  }, [normalizedFinalUrl, fileAnalysis?.isVideo]);

  // Ensure video src is set when controls={false} and shouldSetVideoSrc is true
  useEffect(() => {
    if (!videoRef.current || !fileAnalysis?.isVideo || !normalizedFinalUrl) return;
    if (!controls && shouldSetVideoSrc && videoRef.current.src !== normalizedFinalUrl) {
      videoRef.current.src = normalizedFinalUrl;
      programmaticSrcRef.current = normalizedFinalUrl;
    }
  }, [normalizedFinalUrl, shouldSetVideoSrc, controls, fileAnalysis?.isVideo]);

  // Setup video autoplay for background videos (similar to zoom.tsx)
  useEffect(() => {
    if (!videoRef.current || !fileAnalysis?.isVideo || !shouldAutoplay) return;

    const video = videoRef.current;

    // Force controls to false for autoplay videos
    const shouldShowControls = autoplay ? false : controls;

    // Set video attributes for background playback
    // On mobile, for background videos (controls=false), disable autoplay initially (wait for section tap)
    // On desktop, keep autoplay enabled
    video.autoplay = shouldAutoplay; // Use shouldAutoplay which respects mobile tap-to-play
    video.muted = true; // Always muted for autoplay (required for mobile)
    video.loop = loop;
    video.playsInline = true; // Always true for mobile autoplay
    video.controls = shouldShowControls;
    video.crossOrigin = 'anonymous'; // Required for CORS and optimized loading
    video.setAttribute('playsinline', 'true'); // For iOS compatibility
    video.setAttribute('muted', 'true'); // Also set as attribute for compatibility
    video.setAttribute('autoplay', shouldAutoplay ? 'true' : 'false'); // Conditional autoplay
    video.setAttribute('webkit-playsinline', 'true'); // For older iOS
    video.setAttribute('crossorigin', 'anonymous'); // Also set as attribute for compatibility
    // OPTIMIZED: Use metadata preload for CRITICAL videos, then call load() programmatically
    // This gives better control over when video starts loading
    video.preload = effectivePreload || (isPriorityLoad ? 'metadata' : 'metadata');
    
    // OPTIMIZED: For CRITICAL videos, call load() immediately after setting preload="metadata"
    if (isPriorityLoad && effectivePreload === 'metadata' && normalizedFinalUrl) {
      // Ensure src is set before calling load()
      if (video.src !== normalizedFinalUrl) {
        video.src = normalizedFinalUrl;
        programmaticSrcRef.current = normalizedFinalUrl;
      }
      video.load();
    }

    // Ensure video source loads even if autoplay is blocked (both mobile and desktop)
    // Force load() to ensure video metadata and first frame are loaded
    // Only load if video hasn't loaded yet (readyState < 2) or source has changed
    if (normalizedFinalUrl) {
      const currentSrc = video.src || '';
      // Normalize URLs for comparison (handle both absolute and relative URLs)
      const normalizeUrl = (url: string) => {
        try {
          // If it's already a full URL, extract the pathname + search
          if (url.startsWith('http://') || url.startsWith('https://')) {
            const urlObj = new URL(url);
            return urlObj.pathname + urlObj.search;
          }
          return url;
        } catch {
          return url;
        }
      };
      const normalizedCurrentSrc = normalizeUrl(currentSrc);
      const normalizedTargetSrc = normalizeUrl(normalizedFinalUrl);
      const needsSourceUpdate = !currentSrc || normalizedCurrentSrc !== normalizedTargetSrc;
      const needsLoad = video.readyState < 2; // Only load if video hasn't loaded metadata yet

      // Check if we've already initiated a load for this source recently (within 100ms)
      // This prevents multiple load() calls when useEffect re-runs quickly
      // Use global Map to track loads across all component instances
      const now = Date.now();
      pruneVideoLoadTracker(now);
      const lastLoadTime = videoLoadTracker.get(normalizedTargetSrc);
      const recentlyLoaded = lastLoadTime !== undefined && (now - lastLoadTime) < 100;

      // CRITICAL FIX: Check global deduplication and media cache before loading to prevent duplicate downloads
      // This prevents videos from being loaded multiple times when rendered in different components
      const checkCacheAndLoad = async () => {
        // Check global deduplication first (shared with BackgroundVideo)
        const alreadyHandled = isVideoAlreadyHandled(normalizedFinalUrl);
        if (alreadyHandled) {
          // Another component is already loading this video - just set src and let browser use cache
          if (needsSourceUpdate && video.src !== normalizedFinalUrl) {
            video.src = normalizedFinalUrl;
            programmaticSrcRef.current = normalizedFinalUrl;
            setShouldSetVideoSrc(true);
          }
          return;
        }
        
        // Mark as loading before checking cache
        markVideoLoading(normalizedFinalUrl);
        
        // Check if video is already preloaded or in browser cache
        const isPreloaded = mediaCache.isPreloaded(normalizedFinalUrl);
        const isInBrowserCache = await mediaCache.isInBrowserCache(normalizedFinalUrl);
        
        // If video is already cached/preloaded, just set src and let browser use cache
        if (isPreloaded || isInBrowserCache) {
          markVideoComplete(normalizedFinalUrl, true);
          if (needsSourceUpdate && video.src !== normalizedFinalUrl) {
            video.src = normalizedFinalUrl;
            programmaticSrcRef.current = normalizedFinalUrl;
            setShouldSetVideoSrc(true); // Allow src to be set in JSX
          }
          // Don't call load() if already cached - browser will use cache
          return;
        }

        // Only call load() if source changed AND video hasn't loaded yet, OR if video hasn't loaded metadata
        // AND we haven't already initiated a load for this source recently
        // This prevents reloading videos that are already loaded with the same source
        if (needsSourceUpdate && !recentlyLoaded) {
          video.src = normalizedFinalUrl;
          programmaticSrcRef.current = normalizedFinalUrl; // Track that we set this programmatically
          setShouldSetVideoSrc(true); // Allow src to be set in JSX
          // Record load initiation IMMEDIATELY before load() to prevent race conditions
          markVideoLoadInitiated(normalizedTargetSrc, now);
          video.load();
          // Mark as complete when video loads successfully
          video.addEventListener('loadeddata', () => {
            markVideoComplete(normalizedFinalUrl, true);
          }, { once: true });
          video.addEventListener('error', () => {
            markVideoComplete(normalizedFinalUrl, false);
          }, { once: true });
        } else if (needsLoad && !recentlyLoaded) {
          // Source is same but video hasn't loaded metadata yet, and we haven't recently initiated a load
          setShouldSetVideoSrc(true); // Allow src to be set in JSX
          // Record load initiation IMMEDIATELY before load() to prevent race conditions
          markVideoLoadInitiated(normalizedTargetSrc, now);
          video.load();
          // Mark as complete when video loads successfully
          video.addEventListener('loadeddata', () => {
            markVideoComplete(normalizedFinalUrl, true);
          }, { once: true });
          video.addEventListener('error', () => {
            markVideoComplete(normalizedFinalUrl, false);
          }, { once: true });
        }
      };

      checkCacheAndLoad();
    }

    // Set poster attribute if available (will show while video loads)
    // This will be updated dynamically when the frame is captured
    // Use resolvedPoster (handles responsive poster images)
    const updatePoster = () => {
      if (video && (resolvedPoster || generatedPosterUrl)) {
        video.poster = resolvedPoster || generatedPosterUrl || '';
      }
    };

    updatePoster();

    // Update poster when generatedPosterUrl becomes available
    if (generatedPosterUrl) {
      video.poster = generatedPosterUrl;
    }

    // Try to play the video with retry logic
    let playAttempts = 0;
    const maxPlayAttempts = 5;

    const playVideo = async () => {
      if (!video || !shouldAutoplay) return;

      // Don't retry if we've exceeded max attempts
      if (playAttempts >= maxPlayAttempts) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[S3FilePreview] Video autoplay failed after max attempts');
        }
        // Mark autoplay as blocked if we've exhausted all attempts
        setAutoplayBlocked(true);
        return;
      }

      playAttempts++;

      try {
        // Ensure video is muted (required for autoplay)
        video.muted = true;

        // Check if video has enough data before trying to play
        if (video.readyState < 2 && playAttempts < maxPlayAttempts) {
          // Wait a bit more for data to load
          setTimeout(playVideo, 500);
          return;
        }

        await video.play();
        // Successfully played - mark as played and not blocked
        setHasPlayed(true);
        setAutoplayBlocked(false);
      } catch (error) {
        // Check if it's an autoplay policy error
        const errorName = (error as Error)?.name || '';
        const errorMessage = (error as Error)?.message || '';
        const isAutoplayError = errorName === 'NotAllowedError' ||
          errorName === 'NotSupportedError' ||
          errorMessage.toLowerCase().includes('autoplay') ||
          errorMessage.toLowerCase().includes('play()');

        // Retry if we haven't exceeded max attempts
        if (playAttempts < maxPlayAttempts && !isAutoplayError) {
          // Wait longer between retries
          setTimeout(playVideo, 1000 * playAttempts);
        } else {
          // Mark as blocked if it's an autoplay policy error or we've exhausted retries
          if (isAutoplayError || playAttempts >= maxPlayAttempts) {
            setAutoplayBlocked(true);
          }
          // Silently handle autoplay errors (browser restrictions)
          if (process.env.NODE_ENV === 'development') {
            console.warn('[S3FilePreview] Video autoplay failed after retries:', error);
          }
        }
      }
    };

    // Try to play immediately if video is ready
    if (video.readyState >= 2) { // HAVE_CURRENT_DATA
      playVideo();
    } else {
      // Wait for video to be ready - prioritize canplaythrough for full loading
      video.addEventListener('canplaythrough', playVideo, { once: true });
      video.addEventListener('canplay', playVideo, { once: true });
      video.addEventListener('loadedmetadata', playVideo, { once: true });
      video.addEventListener('loadeddata', playVideo, { once: true });
    }

    // Also try after a short delay to catch dynamically loaded videos
    const timeoutId = setTimeout(playVideo, 500);

    // Fallback timer: if video is still paused after a delay, autoplay is likely blocked
    const autoplayCheckTimeout = setTimeout(() => {
      if (video && video.paused && !hasPlayed && document.visibilityState === 'visible') {
        setAutoplayBlocked(true);
        if (process.env.NODE_ENV === 'development') {
          console.warn('[S3FilePreview] Autoplay appears to be blocked - video still paused after delay');
        }
        // Try one more time to play programmatically
        if (video.readyState >= 2) {
          video.play().catch(() => {
            // Silently handle - autoplay is blocked
          });
        }
      }
    }, 1500);

    // Global user interaction handler for mobile autoplay
    // On mobile, autoplay requires user interaction, so we listen for the first touch/click
    // IMPORTANT: This must happen synchronously during the touch event for mobile autoplay
    // CRITICAL: For SharedVideoBackground videos without parent sections, this handler enables autoplay
    let userInteracted = false;
    const handleUserInteraction = () => {
      if (userInteracted || !video) return;
      
      // Enable autoplay if it was disabled initially (e.g., on mobile for background videos)
      // This is critical for SharedVideoBackground videos that don't have parent sections
      let shouldProceed = shouldAutoplay || autoplay;
      if (!shouldAutoplay && autoplay) {
        setShouldAutoplay(true);
        video.autoplay = true;
        video.setAttribute('autoplay', 'true');
        shouldProceed = true; // We just enabled it, so proceed
      }
      
      // Only proceed if autoplay is enabled or should be enabled
      if (!shouldProceed) return;
      
      userInteracted = true;

      // Ensure video attributes are set for mobile
      video.muted = true;
      video.setAttribute('muted', 'true');
      video.setAttribute('playsinline', 'true');
      video.setAttribute('webkit-playsinline', 'true');
      video.playsInline = true;

      // Ensure video source is loaded before trying to play (both mobile and desktop)
      if (normalizedFinalUrl) {
        // Normalize URLs for comparison
        const normalizeUrl = (url: string) => {
          try {
            if (url.startsWith('http://') || url.startsWith('https://')) {
              const urlObj = new URL(url);
              return urlObj.pathname + urlObj.search;
            }
            return url;
          } catch {
            return url;
          }
        };
        const normalizedCurrentSrc = normalizeUrl(video.src || '');
        const normalizedTargetSrc = normalizeUrl(normalizedFinalUrl);
        const needsSourceUpdate = !video.src || normalizedCurrentSrc !== normalizedTargetSrc;

        // Check if we've already initiated a load for this source recently
        const now = Date.now();
        pruneVideoLoadTracker(now);
        const lastLoadTime = videoLoadTracker.get(normalizedTargetSrc);
        const recentlyLoaded = lastLoadTime !== undefined && (now - lastLoadTime) < 100;

        // CRITICAL FIX: Check global deduplication and media cache before loading to prevent duplicate downloads
        const checkCacheAndLoad = async () => {
          // Check global deduplication first (shared with BackgroundVideo)
          const alreadyHandled = isVideoAlreadyHandled(normalizedFinalUrl);
          if (alreadyHandled) {
            // Another component is already loading this video - just set src and let browser use cache
            if (needsSourceUpdate && video.src !== normalizedFinalUrl) {
              video.src = normalizedFinalUrl;
              programmaticSrcRef.current = normalizedFinalUrl;
              setShouldSetVideoSrc(true);
            }
            return;
          }
          
          // Mark as loading before checking cache
          markVideoLoading(normalizedFinalUrl);
          
          // Check if video is already preloaded or in browser cache
          const isPreloaded = mediaCache.isPreloaded(normalizedFinalUrl);
          const isInBrowserCache = await mediaCache.isInBrowserCache(normalizedFinalUrl);
          
          // If video is already cached/preloaded, just set src and let browser use cache
          if (isPreloaded || isInBrowserCache) {
            markVideoComplete(normalizedFinalUrl, true);
            if (needsSourceUpdate && video.src !== normalizedFinalUrl) {
              video.src = normalizedFinalUrl;
              programmaticSrcRef.current = normalizedFinalUrl;
              setShouldSetVideoSrc(true); // Allow src to be set in JSX
            }
            // Don't call load() if already cached - browser will use cache
            return;
          }

          // Ensure source is set, but only load if needed and not recently loaded
          if (needsSourceUpdate && !recentlyLoaded) {
            video.src = normalizedFinalUrl;
            programmaticSrcRef.current = normalizedFinalUrl; // Track that we set this programmatically
            setShouldSetVideoSrc(true); // Allow src to be set in JSX
            markVideoLoadInitiated(normalizedTargetSrc, now);
            video.load();
            // Mark as complete when video loads successfully
            video.addEventListener('loadeddata', () => {
              markVideoComplete(normalizedFinalUrl, true);
            }, { once: true });
            video.addEventListener('error', () => {
              markVideoComplete(normalizedFinalUrl, false);
            }, { once: true });
          }
        };

        checkCacheAndLoad();
        // Wait for video to have some data before playing
        if (video.readyState < 2) {
          const tryPlayWhenReady = () => {
            video.play().catch(() => {
              // Silently handle errors
            });
          };
          video.addEventListener('canplay', tryPlayWhenReady, { once: true });
          video.addEventListener('loadeddata', tryPlayWhenReady, { once: true });
          return;
        }
      }

      // Try to play immediately - must be synchronous during user interaction
      const playPromise = video.play();

      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          // If immediate play fails, try again when ready
          const tryPlayWhenReady = () => {
            video.play().catch(() => {
              // Silently handle errors
            });
          };

          if (video.readyState >= 2) {
            tryPlayWhenReady();
          } else {
            video.addEventListener('canplay', tryPlayWhenReady, { once: true });
            video.addEventListener('loadeddata', tryPlayWhenReady, { once: true });
          }
        });
      }
    };

    // On mobile, for background videos (controls=false) in Hero sections, listen for section-level taps
    // On desktop or for videos with controls, use global document handlers
    let sectionTapHandler: ((e: TouchEvent | MouseEvent) => void) | null = null;
    let sectionElement: HTMLElement | null = null;
    
    if (isMobile && !controls && autoplay) {
      // Find the parent section element (Hero section)
      const findParentSection = (element: HTMLElement | null): HTMLElement | null => {
        if (!element) return null;
        if (element.tagName === 'SECTION' || element.getAttribute('id') === 'hero') {
          return element;
        }
        return findParentSection(element.parentElement);
      };

      sectionElement = videoRef.current ? findParentSection(videoRef.current.parentElement) : null;
      
      if (sectionElement) {
        // Listen for taps specifically on the Hero section
        sectionTapHandler = (e: TouchEvent | MouseEvent) => {
          if (!video || sectionTapPlayAttemptedRef.current) return;
          
          // Check if tap is within the section
          const target = e.target as HTMLElement;
          if (!sectionElement || !sectionElement.contains(target)) return;

          // Mark that we've attempted to play from section tap
          sectionTapPlayAttemptedRef.current = true;

          // Enable autoplay and play video immediately (within user interaction context)
          setShouldAutoplay(true);
          video.autoplay = true;
          video.muted = true;
          video.setAttribute('muted', 'true');
          video.setAttribute('autoplay', 'true');
          video.setAttribute('playsinline', 'true');
          video.setAttribute('webkit-playsinline', 'true');
          video.playsInline = true;

          // Play immediately - must be synchronous during user interaction
          const playPromise = video.play();
          if (playPromise !== undefined) {
            playPromise.then(() => {
              setHasPlayed(true);
              setAutoplayBlocked(false);
            }).catch((error) => {
              if (process.env.NODE_ENV === 'development') {
                console.warn('[S3FilePreview] Mobile section tap play failed:', error);
              }
              // Try again when video is ready
              if (video.readyState >= 2) {
                video.play().then(() => {
                  setHasPlayed(true);
                  setAutoplayBlocked(false);
                }).catch(() => {});
              } else {
                const tryPlayWhenReady = () => {
                  video.play().then(() => {
                    setHasPlayed(true);
                    setAutoplayBlocked(false);
                  }).catch(() => {});
                };
                video.addEventListener('canplay', tryPlayWhenReady, { once: true });
                video.addEventListener('loadeddata', tryPlayWhenReady, { once: true });
              }
            });
          }
        };

        // Listen for touch events on the section (capture phase for immediate response)
        sectionElement.addEventListener('touchstart', sectionTapHandler as EventListener, { once: true, capture: true });
        sectionElement.addEventListener('touchend', sectionTapHandler as EventListener, { once: true, capture: true });
      }
    }

    // Listen for user interactions (touch/click) in capture phase for immediate response
    // Use capture: true to catch events early, before they bubble
    // CRITICAL: Use passive: false for touch events to enable synchronous play() calls on mobile
    // Only use global handlers if not using section-specific handlers above
    const events: Array<'touchstart' | 'touchend' | 'click' | 'mousedown'> = sectionTapHandler 
      ? [] // Skip global handlers if using section-specific handlers
      : ['touchstart', 'touchend', 'click', 'mousedown'];
    
    events.forEach(event => {
      // For touch events, use non-passive listeners to enable synchronous play() calls
      const isTouchEvent = event === 'touchstart' || event === 'touchend';
      const options = isTouchEvent 
        ? { once: true, capture: true, passive: false } as const
        : { once: true, capture: true } as const;
      document.addEventListener(event, handleUserInteraction, options);
    });

    // Listen for video play events to detect when video starts playing
    const handlePlay = () => {
      setHasPlayed(true);
      setAutoplayBlocked(false);
    };

    const handlePause = () => {
      // Only mark as blocked if we expected it to be playing
      if (shouldAutoplay && !video.ended) {
        // Don't mark as blocked on pause - might be intentional
      }
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('playing', handlePlay);
    video.addEventListener('pause', handlePause);

    // Integrate with global autoplay trigger system
    // Listen for custom event that VideoAutoplayConsent component dispatches
    // CRITICAL: This handler enables autoplay for videos that had it disabled initially on mobile
    const handleGlobalAutoplayTrigger = () => {
      if (!video || hasPlayed) return;
      
      // Enable autoplay if it was disabled initially (e.g., on mobile for background videos)
      // This is critical for SharedVideoBackground videos that don't have parent sections
      if (!shouldAutoplay && autoplay) {
        setShouldAutoplay(true);
        video.autoplay = true;
        video.setAttribute('autoplay', 'true');
      }
      
      // Ensure video attributes are set for mobile autoplay
      video.muted = true;
      video.setAttribute('muted', 'true');
      video.setAttribute('playsinline', 'true');
      video.setAttribute('webkit-playsinline', 'true');
      video.playsInline = true;

      // Ensure video source is loaded before trying to play (both mobile and desktop)
      if (normalizedFinalUrl) {
        // Normalize URLs for comparison
        const normalizeUrl = (url: string) => {
          try {
            if (url.startsWith('http://') || url.startsWith('https://')) {
              const urlObj = new URL(url);
              return urlObj.pathname + urlObj.search;
            }
            return url;
          } catch {
            return url;
          }
        };
        const normalizedCurrentSrc = normalizeUrl(video.src || '');
        const normalizedTargetSrc = normalizeUrl(normalizedFinalUrl);
        const needsSourceUpdate = !video.src || normalizedCurrentSrc !== normalizedTargetSrc;

        // Check if we've already initiated a load for this source recently
        const now = Date.now();
        pruneVideoLoadTracker(now);
        const lastLoadTime = videoLoadTracker.get(normalizedTargetSrc);
        const recentlyLoaded = lastLoadTime !== undefined && (now - lastLoadTime) < 100;

        // CRITICAL FIX: Check global deduplication and media cache before loading
        const checkCacheAndLoad = async () => {
          // Check global deduplication first
          const alreadyHandled = isVideoAlreadyHandled(normalizedFinalUrl);
          if (alreadyHandled) {
            // Another component is already loading this video - just set src and let browser use cache
            if (needsSourceUpdate && video.src !== normalizedFinalUrl) {
              video.src = normalizedFinalUrl;
              programmaticSrcRef.current = normalizedFinalUrl;
              setShouldSetVideoSrc(true);
            }
            // Try to play if video has enough data
            if (video.readyState >= 2) {
              video.play().then(() => {
                setHasPlayed(true);
                setAutoplayBlocked(false);
              }).catch(() => {});
            } else {
              // Wait for video to load before playing
              const tryPlayWhenReady = () => {
                video.play().then(() => {
                  setHasPlayed(true);
                  setAutoplayBlocked(false);
                }).catch(() => {});
              };
              video.addEventListener('canplay', tryPlayWhenReady, { once: true });
              video.addEventListener('loadeddata', tryPlayWhenReady, { once: true });
            }
            return;
          }
          
          // Mark as loading before checking cache
          markVideoLoading(normalizedFinalUrl);
          
          // Check if video is already preloaded or in browser cache
          const isPreloaded = mediaCache.isPreloaded(normalizedFinalUrl);
          const isInBrowserCache = await mediaCache.isInBrowserCache(normalizedFinalUrl);
          
          // If video is already cached/preloaded, just set src and let browser use cache
          if (isPreloaded || isInBrowserCache) {
            markVideoComplete(normalizedFinalUrl, true);
            if (needsSourceUpdate && video.src !== normalizedFinalUrl) {
              video.src = normalizedFinalUrl;
              programmaticSrcRef.current = normalizedFinalUrl;
              setShouldSetVideoSrc(true);
            }
            // Try to play if video has enough data
            if (video.readyState >= 2) {
              video.play().then(() => {
                setHasPlayed(true);
                setAutoplayBlocked(false);
              }).catch(() => {});
            } else {
              // Wait for video to load before playing
              const tryPlayWhenReady = () => {
                video.play().then(() => {
                  setHasPlayed(true);
                  setAutoplayBlocked(false);
                }).catch(() => {});
              };
              video.addEventListener('canplay', tryPlayWhenReady, { once: true });
              video.addEventListener('loadeddata', tryPlayWhenReady, { once: true });
            }
            return;
          }

          // Ensure source is set, but only load if needed and not recently loaded
          if (needsSourceUpdate && !recentlyLoaded) {
            video.src = normalizedFinalUrl;
            programmaticSrcRef.current = normalizedFinalUrl;
            setShouldSetVideoSrc(true);
            markVideoLoadInitiated(normalizedTargetSrc, now);
            video.load();
            // Mark as complete when video loads successfully
            video.addEventListener('loadeddata', () => {
              markVideoComplete(normalizedFinalUrl, true);
              // Try to play once video is loaded
              video.play().then(() => {
                setHasPlayed(true);
                setAutoplayBlocked(false);
              }).catch(() => {});
            }, { once: true });
            video.addEventListener('error', () => {
              markVideoComplete(normalizedFinalUrl, false);
            }, { once: true });
            return;
          }
          
          // Source is set and recently loaded, try to play if ready
          if (video.readyState >= 2) {
            video.play().then(() => {
              setHasPlayed(true);
              setAutoplayBlocked(false);
            }).catch(() => {});
          } else {
            // Wait for video to have some data before playing
            const tryPlayWhenReady = () => {
              video.play().then(() => {
                setHasPlayed(true);
                setAutoplayBlocked(false);
              }).catch(() => {});
            };
            video.addEventListener('canplay', tryPlayWhenReady, { once: true });
            video.addEventListener('loadeddata', tryPlayWhenReady, { once: true });
          }
        };

        checkCacheAndLoad();
        return; // Exit early after handling source loading
      }

      // If no normalizedFinalUrl, try to play directly
      if (video.readyState >= 2) {
        // Video has enough data, try to play
        video.play().then(() => {
          setHasPlayed(true);
          setAutoplayBlocked(false);
        }).catch(() => {
          // Silently handle - will be caught by other handlers
        });
      } else {
        // Wait for video to have some data before playing
        const tryPlayWhenReady = () => {
          video.play().then(() => {
            setHasPlayed(true);
            setAutoplayBlocked(false);
          }).catch(() => {});
        };
        video.addEventListener('canplay', tryPlayWhenReady, { once: true });
        video.addEventListener('loadeddata', tryPlayWhenReady, { once: true });
        video.addEventListener('loadedmetadata', tryPlayWhenReady, { once: true });
      }
    };

    // Listen for global autoplay trigger events
    document.addEventListener('video-autoplay-trigger', handleGlobalAutoplayTrigger);

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      clearTimeout(autoplayCheckTimeout);
      video.removeEventListener('canplaythrough', playVideo);
      video.removeEventListener('canplay', playVideo);
      video.removeEventListener('loadedmetadata', playVideo);
      video.removeEventListener('loadeddata', playVideo);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('playing', handlePlay);
      video.removeEventListener('pause', handlePause);
      document.removeEventListener('video-autoplay-trigger', handleGlobalAutoplayTrigger);
      events.forEach(event => {
        // Use same options when removing as when adding
        const isTouchEvent = event === 'touchstart' || event === 'touchend';
        const options = isTouchEvent 
          ? { once: true, capture: true, passive: false } as const
          : { once: true, capture: true } as const;
        document.removeEventListener(event, handleUserInteraction, options);
      });
      // Cleanup section-specific handlers
      if (sectionElement && sectionTapHandler) {
        sectionElement.removeEventListener('touchstart', sectionTapHandler as EventListener, true);
        sectionElement.removeEventListener('touchend', sectionTapHandler as EventListener, true);
      }
    };
  }, [normalizedFinalUrl, fileAnalysis?.isVideo, shouldAutoplay, muted, loop, playsInline, controls, preload, poster, generatedPosterUrl, isMobile]);

  // Ensure video attributes are set for both mobile and desktop
  // (but don't call load() here - that's handled in the autoplay useEffect)
  useEffect(() => {
    if (!videoRef.current || !fileAnalysis?.isVideo) return;

    const video = videoRef.current;

    // Ensure video attributes are set for autoplay (both mobile and desktop)
    video.muted = true;
    video.setAttribute('muted', 'true');
    video.setAttribute('playsinline', 'true');
    video.setAttribute('webkit-playsinline', 'true');
    video.playsInline = true;
    // OPTIMIZED: Use metadata preload for all videos, then call load() programmatically for priority
    video.preload = effectivePreload || (isPriorityLoad ? 'metadata' : 'metadata');
  }, [fileAnalysis?.isVideo, preload, isPriorityLoad, effectivePreload]);

  // Expose video ref to parent component via callback
  // Use a ref to track the last callback and ref value to avoid infinite loops
  const lastCallbackRef = useRef<((ref: HTMLVideoElement | null) => void) | null>(null);
  const lastRefValueRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (!videoRefCallback) {
      // Cleanup if callback is removed
      if (lastCallbackRef.current && lastRefValueRef.current) {
        lastCallbackRef.current(null);
        lastRefValueRef.current = null;
      }
      lastCallbackRef.current = null;
      return;
    }

    // Only call callback if:
    // 1. We have a video ref
    // 2. The callback has changed OR the ref value has changed
    const currentRef = videoRef.current;
    const callbackChanged = lastCallbackRef.current !== videoRefCallback;
    const refChanged = lastRefValueRef.current !== currentRef;

    if (currentRef && (callbackChanged || refChanged)) {
      videoRefCallback(currentRef);
      lastCallbackRef.current = videoRefCallback;
      lastRefValueRef.current = currentRef;
    }

    // Also call with null when component unmounts or video is removed
    return () => {
      if (lastCallbackRef.current && lastRefValueRef.current) {
        lastCallbackRef.current(null);
        lastRefValueRef.current = null;
      }
      lastCallbackRef.current = null;
    };
  }, [videoRefCallback, normalizedFinalUrl, fileAnalysis?.isVideo]);

  // Update video poster attribute when generatedPosterUrl becomes available
  useEffect(() => {
    if (videoRef.current && generatedPosterUrl && !poster) {
      videoRef.current.poster = generatedPosterUrl;
    }
  }, [generatedPosterUrl, poster]);

  // Fallback: Ensure video shows visible frame even if poster generation fails
  // This ensures the video element itself displays a visible frame as a fallback
  useEffect(() => {
    if (!fileAnalysis?.isVideo || !videoRef.current || !normalizedFinalUrl || controls) return;

    const video = videoRef.current;
    // Target frame time for fallback (1 second to avoid black intro frames)
    const FALLBACK_FRAME_TIME = 1.0;

    // If no poster is available (neither provided nor generated), ensure video shows visible frame
    if (!poster && !generatedPosterUrl) {
      const ensureVisibleFrame = () => {
        // Wait for video to have enough data before seeking
        if (video.readyState >= 2) {
          // Seek to 1 second to avoid black intro frames
          const targetTime = video.duration ? Math.min(FALLBACK_FRAME_TIME, video.duration / 2) : FALLBACK_FRAME_TIME;
          if (Math.abs(video.currentTime - targetTime) > 0.1) {
            video.currentTime = targetTime;
          }
        }
      };

      // Try to show visible frame when video has loaded enough
      if (video.readyState >= 2) {
        ensureVisibleFrame();
      } else {
        const handleReady = () => {
          ensureVisibleFrame();
          video.removeEventListener('loadeddata', handleReady);
          video.removeEventListener('canplay', handleReady);
          video.removeEventListener('canplaythrough', handleReady);
        };

        video.addEventListener('loadeddata', handleReady, { once: true });
        video.addEventListener('canplay', handleReady, { once: true });
        video.addEventListener('canplaythrough', handleReady, { once: true });
      }
    }
  }, [fileAnalysis?.isVideo, normalizedFinalUrl, poster, generatedPosterUrl, controls]);

  // Extract first frame EARLY using a hidden video element (before main video loads)
  // PERFORMANCE: Uses global videoPosterCache to prevent duplicate extractions
  // This prevents the same video from being downloaded multiple times for poster generation
  useEffect(() => {
    if (!fileAnalysis?.isVideo || poster || !normalizedFinalUrl || typeof normalizedFinalUrl !== 'string') return;
    if (isExtractingFrame.current || generatedPosterUrl) return; // Already extracting or extracted

    // PERFORMANCE FIX: Check global cache first to prevent duplicate video loads
    const cachedPoster = videoPosterCache.getPoster(normalizedFinalUrl);
    if (cachedPoster) {
      setGeneratedPosterUrl(cachedPoster);
      if (videoRef.current) {
        videoRef.current.poster = cachedPoster;
      }
      return;
    }

    // Check if another component is already extracting this poster
    if (videoPosterCache.isExtracting(normalizedFinalUrl)) {
      // Wait for the other component to finish and then use the cached result
      const checkCache = setInterval(() => {
        const poster = videoPosterCache.getPoster(normalizedFinalUrl);
        if (poster) {
          clearInterval(checkCache);
          setGeneratedPosterUrl(poster);
          if (videoRef.current) {
            videoRef.current.poster = poster;
          }
        }
      }, 200);
      
      // Stop checking after 5 seconds
      setTimeout(() => clearInterval(checkCache), 5000);
      return;
    }

    // Extract poster using the global cache
    const extractPoster = async (): Promise<string | null> => {
      return new Promise((resolve) => {
        // Create hidden video element for frame extraction
        const hiddenVideo = document.createElement('video');
        hiddenVideo.style.position = 'absolute';
        hiddenVideo.style.visibility = 'hidden';
        hiddenVideo.style.width = '1px';
        hiddenVideo.style.height = '1px';
        hiddenVideo.style.pointerEvents = 'none';
        hiddenVideo.crossOrigin = 'anonymous';
        hiddenVideo.preload = 'metadata';
        hiddenVideo.muted = true;
        hiddenVideo.playsInline = true;
        
        document.body.appendChild(hiddenVideo);
        hiddenVideoRef.current = hiddenVideo;

        let captured = false;
        let retryCount = 0;
        const maxRetries = 3;
        let timeoutId: NodeJS.Timeout | null = null;

        const cleanup = () => {
          if (timeoutId) clearTimeout(timeoutId);
          if (hiddenVideo.parentNode) {
            hiddenVideo.parentNode.removeChild(hiddenVideo);
          }
          hiddenVideoRef.current = null;
        };

        const captureFrame = () => {
          if (captured) return;

          try {
            if (hiddenVideo.videoWidth <= 0 || hiddenVideo.videoHeight <= 0) {
              if (retryCount < maxRetries && hiddenVideo.readyState >= 1) {
                retryCount++;
                timeoutId = setTimeout(captureFrame, 500);
              } else {
                cleanup();
                resolve(null);
              }
              return;
            }

            const canvas = document.createElement('canvas');
            canvas.width = hiddenVideo.videoWidth;
            canvas.height = hiddenVideo.videoHeight;
            const ctx = canvas.getContext('2d');

            if (ctx) {
              ctx.drawImage(hiddenVideo, 0, 0, canvas.width, canvas.height);
              canvas.toBlob((blob) => {
                if (blob && !captured) {
                  const posterUrl = URL.createObjectURL(blob);
                  captured = true;
                  cleanup();
                  resolve(posterUrl);
                } else if (!captured && retryCount < maxRetries) {
                  retryCount++;
                  timeoutId = setTimeout(captureFrame, 500);
                } else {
                  cleanup();
                  resolve(null);
                }
              }, 'image/jpeg', 0.8);
            } else {
              cleanup();
              resolve(null);
            }
          } catch (_error) {
            if (retryCount < maxRetries) {
              retryCount++;
              timeoutId = setTimeout(captureFrame, 500);
            } else {
              cleanup();
              resolve(null);
            }
          }
        };

        const POSTER_FRAME_TIME = 1.0;

        const attemptCapture = () => {
          if (hiddenVideo.readyState < 2) {
            const waitForData = () => {
              if (hiddenVideo.readyState >= 2 && !captured) {
                attemptCapture();
              } else if (!captured) {
                setTimeout(waitForData, 200);
              }
            };
            waitForData();
            return;
          }

          if (!hiddenVideo.paused) hiddenVideo.pause();

          const targetTime = hiddenVideo.duration 
            ? Math.min(POSTER_FRAME_TIME, hiddenVideo.duration / 2) 
            : POSTER_FRAME_TIME;

          if (Math.abs(hiddenVideo.currentTime - targetTime) > 0.1) {
            hiddenVideo.currentTime = targetTime;
          }

          hiddenVideo.addEventListener('seeked', () => {
            setTimeout(() => { if (!captured) captureFrame(); }, 100);
          }, { once: true });

          setTimeout(() => { if (!captured) captureFrame(); }, 500);
        };

        const handleReady = () => attemptCapture();

        hiddenVideo.addEventListener('loadedmetadata', handleReady, { once: true });
        hiddenVideo.addEventListener('loadeddata', handleReady, { once: true });
        hiddenVideo.addEventListener('canplay', handleReady, { once: true });

        // Check deduplication before loading hidden video for frame extraction
        const alreadyHandled = isVideoAlreadyHandled(normalizedFinalUrl);
        if (alreadyHandled) {
          // Another component is already loading this video - skip frame extraction to avoid duplicate request
          cleanup();
          resolve(null);
          return;
        }
        
        // Mark as loading before setting src
        markVideoLoading(normalizedFinalUrl);
        
        hiddenVideo.src = normalizedFinalUrl;
        hiddenVideo.load();

        // Timeout fallback
        setTimeout(() => {
          if (!captured) {
            cleanup();
            resolve(null);
          }
        }, 5000);
      });
    };

    isExtractingFrame.current = true;

    // Use the global cache for extraction
    videoPosterCache.getOrExtractPoster(normalizedFinalUrl, extractPoster)
      .then((posterUrl) => {
        isExtractingFrame.current = false;
        if (posterUrl) {
          setGeneratedPosterUrl(posterUrl);
          if (videoRef.current) {
            videoRef.current.poster = posterUrl;
          }
        }
      })
      .catch(() => {
        isExtractingFrame.current = false;
      });

    // Cleanup (note: blob URL is managed by videoPosterCache)
    return () => {
      if (hiddenVideoRef.current?.parentNode) {
        hiddenVideoRef.current.parentNode.removeChild(hiddenVideoRef.current);
      }
      hiddenVideoRef.current = null;
      isExtractingFrame.current = false;
    };
  }, [fileAnalysis?.isVideo, poster, normalizedFinalUrl, effectiveSrc]);


  // Preload image only when in view (lazy loading) and check cache first
  useEffect(() => {
    // Only preload images that are in view or above the fold
    if (!normalizedFinalUrl || !fileAnalysis?.isImage) return;

    // Skip lazy loading for high priority content only
    if (shouldLoadImmediately) {
      setIsInView(true);
    }

    // Only preload when in view or high priority
    if (!isInView && !shouldLoadImmediately) return;

    // Check if image is already in browser cache before preloading
    const checkCacheAndPreload = async () => {
      // Quick cache check - if image is already loaded, don't trigger another request
      const testImg = new window.Image();
      let isCached = false;

      const cacheCheck = new Promise<boolean>((resolve) => {
        testImg.onload = () => {
          // If onload fires quickly, it's likely cached
          resolve(true);
        };
        testImg.onerror = () => resolve(false);
        testImg.src = normalizedFinalUrl;

        // Timeout after 50ms - cached images load almost instantly
        setTimeout(() => {
          if (testImg.complete && testImg.naturalWidth > 0) {
            resolve(true);
          } else {
            resolve(false);
          }
        }, 50);
      });

      isCached = await cacheCheck;

      // If not cached, proceed with normal preload
      if (!isCached) {
        const img = new window.Image();
        img.onload = () => {
          // Image loaded successfully
        };
        img.onerror = () => {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[S3FilePreview] Image preload failed:', normalizedFinalUrl);
          }
        };
        img.src = normalizedFinalUrl;
      }
      // If cached, image will load naturally when rendered - no need to preload
    };

    checkCacheAndPreload();
  }, [normalizedFinalUrl, fileAnalysis?.isImage, isInView, autoplay, priority]);

  // Removed: Escape key handling is now in VideoModal component
  // Removed: iOS fullscreen exit handling is now in VideoModal component

  // Removed: Modal sidebar offset calculation - modal is now rendered at PageRenderer level

  // Show placeholder for empty/invalid src
  const isEmptySrc = !effectiveSrc || typeof effectiveSrc !== 'string' || effectiveSrc.trim() === '';

  if (isEmptySrc) {
    return (
      <div className={`flex flex-col items-center justify-center w-full h-full bg-gray-100 border-2 border-dashed border-gray-300 rounded ${className}`} style={style}>
        <Suspense fallback={<div className="w-12 h-12 bg-gray-300 rounded mb-2" />}>
          <Camera className="w-12 h-12 text-gray-400 mb-2" />
        </Suspense>
        <span className="text-sm font-medium text-gray-600">No media</span>
        <span className="text-xs text-gray-500">Upload a file to preview</span>
      </div>
    );
  }

  // Handler for successful image load
  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    try {
      const target = event.currentTarget as HTMLImageElement;

      // Store reference to the image element
      imageRef.current = target;

      setIsLoading(false);

      // Call external onLoad callback if provided
      if (onLoad) {
        onLoad();
      }

      // Capture image dimensions and calculate aspect ratio
      if (target.naturalWidth && target.naturalHeight) {
        const dimensions = {
          width: target.naturalWidth,
          height: target.naturalHeight
        };

        // Calculate aspect ratio
        const ratio = dimensions.width / dimensions.height;
        setAspectRatio(ratio);

        // Determine objectFit based on aspect ratio
        const is9to16 = Math.abs(ratio - 0.5625) < 0.01;
        const is16to9 = Math.abs(ratio - 1.7777777777777777) < 0.01;
        setComputedObjectFit((is9to16 || is16to9) ? 'contain' : 'cover');

        // Call the callback if provided
        if (onDimensionsLoaded) {
          onDimensionsLoaded(dimensions);
        }
      }

      if (process.env.NODE_ENV === 'development') {
        console.log("[S3FilePreview] Image loaded successfully", target.naturalWidth, "x", target.naturalHeight);
      }
    } catch {
      setIsLoading(false);
      if (process.env.NODE_ENV === 'development') {
        console.warn("[S3FilePreview] Image loaded but logging failed");
      }
    }
  };

  // Handler for image error - completely silent to prevent Next.js unhandled errors
  const handleImageError = async (error?: React.SyntheticEvent<HTMLImageElement, Event>) => {
    // Prevent the error from bubbling up and being treated as unhandled
    if (error) {
      try {
        error.preventDefault();
        error.stopPropagation();
      } catch {
        // Silently handle any preventDefault/stopPropagation errors
      }
    }

    setIsLoading(false);
    setImageError(true);

    // Check if it's a 404/CDN error by trying to fetch the URL
    if (normalizedFinalUrl && typeof normalizedFinalUrl === 'string' && normalizedFinalUrl.includes('/api/media/download')) {
      try {
        const response = await fetch(normalizedFinalUrl, { method: 'HEAD' });
        if (response.status === 404) {
          setFileNotFound(true);
          // CACHE FIX: Invalidate the cache for this URL so next load fetches fresh
          // This handles the case where admin deleted the file from S3
          await mediaCache.invalidateUrl(normalizedFinalUrl);
        } else if (!corsRetryAttempted && !normalizedFinalUrl.includes('nocdn=true') && shouldRetryWithNoCdn(response.status)) {
          console.warn('[S3FilePreview] CDN returned error status - retrying with direct API access (nocdn=true)', {
            url: normalizedFinalUrl,
            status: response.status
          });
          setCorsRetryAttempted(true);
          return;
        }
      } catch (fetchError) {
        // If fetch fails, assume it might be a 404
        if (!corsRetryAttempted && !normalizedFinalUrl.includes('nocdn=true')) {
          console.warn('[S3FilePreview] CDN HEAD failed - retrying with direct API access (nocdn=true)', {
            url: normalizedFinalUrl,
            error: fetchError instanceof Error ? fetchError.message : String(fetchError)
          });
          setCorsRetryAttempted(true);
          return;
        }
        if (normalizedFinalUrl.includes('/api/media/download')) {
          setFileNotFound(true);
          // Also invalidate cache on fetch failure
          await mediaCache.invalidateUrl(normalizedFinalUrl);
        }
      }
    }

    // Call external onError callback if provided
    if (onError) {
      onError(error);
    }

    try {
      const loadTime = Date.now() - loadStartTime;

      // Only log in development mode to avoid console errors in production
      if (process.env.NODE_ENV === 'development') {
        console.warn("[S3FilePreview] Error loading image");
        console.warn("- Source URL:", effectiveSrc || 'unknown');
        console.warn("- Final URL:", safeFinalUrl || 'unknown');
        console.warn("- Is S3 URL:", safeIsS3Url);
        console.warn("- S3 Key:", safeS3Key || 'unknown');
        console.warn("- Error Type:", error?.type || 'unknown');
        console.warn("- File Name:", fileName || 'unknown');
        console.warn("- Failed after:", `${loadTime}ms`);
        console.warn("- Timestamp:", new Date().toISOString());
      }
    } catch {
      // Completely silent fallback - no logging to avoid any console errors
    }
  };

  // Mostrar nada si no hay URL o análisis
  if (!effectiveSrc || !fileAnalysis) {
    return null;
  }

  const { isImage, isPdf, isVideo, fileCategory } = fileAnalysis;
  // Disable preview modal for videos with autoplay (background videos) or if explicitly disabled
  const shouldEnablePreviewModal = !disablePreviewModal && (isImage || (isVideo && !shouldAutoplay));
  const displayFileName = fileName || (typeof effectiveSrc === 'string' ? effectiveSrc.split('/').pop() : 'download') || 'download';

  // Generate poster URL for videos if not provided
  // Use provided poster (responsive or string), server-generated thumbnail, generated poster from first frame, or undefined
  // resolvedPoster already handles responsive poster images (mobile/desktop), so check if it's a video URL
  const resolvedPosterForVideo = useMemo(() => {
    // Use resolvedPoster (handles responsive images) as base
    const basePoster = resolvedPoster;
    if (!basePoster) return null;
    
    // Check if poster is actually a video URL
    const mediaType = getMediaTypeFromUrl(basePoster);
    if (mediaType === 'video') {
      // Check videoPosterCache for cached poster
      const cachedPoster = videoPosterCache.getPoster(basePoster);
      if (cachedPoster) {
        return cachedPoster;
      }
      // Also check if normalizedFinalUrl has a cached poster
      if (normalizedFinalUrl) {
        const cachedPosterFromVideo = videoPosterCache.getPoster(normalizedFinalUrl);
        if (cachedPosterFromVideo) {
          return cachedPosterFromVideo;
        }
      }
      return null; // Don't use video URL as poster
    }
    // It's an actual image URL, use it
    return basePoster;
  }, [resolvedPoster, normalizedFinalUrl]);
  
  const videoPoster = resolvedPosterForVideo || serverThumbnailUrl || generatedPosterUrl || undefined;
  
  // Track when poster image first appears
  useEffect(() => {
    if (videoPoster && posterImageStartTime.current === null) {
      posterImageStartTime.current = Date.now();
    }
  }, [videoPoster]);
  
  // Check if we can hide poster image (video loaded + minimum 3 seconds)
  useEffect(() => {
    if (!videoPoster || !videoReadyForPlayback) {
      setCanHidePoster(false);
      return;
    }
    
    const checkCanHide = () => {
      if (posterImageStartTime.current === null) {
        setCanHidePoster(false);
        return;
      }
      
      const elapsed = Date.now() - posterImageStartTime.current;
      const MIN_DISPLAY_TIME = 3000; // 3 seconds minimum
      
      if (elapsed >= MIN_DISPLAY_TIME && videoReadyForPlayback) {
        setCanHidePoster(true);
      } else {
        // Check again after remaining time
        const remainingTime = MIN_DISPLAY_TIME - elapsed;
        if (remainingTime > 0) {
          setTimeout(checkCanHide, remainingTime);
        }
      }
    };
    
    checkCanHide();
  }, [videoPoster, videoReadyForPlayback]);
  

  // File icon rendering moved to FileIconRenderer component
  const renderFileIcon = () => <FileIconRenderer fileCategory={fileCategory} />;

  const shouldLoadMedia = priority || isInView;

  const renderSkeletonContainer = () => (
    <div className={`relative w-full h-full overflow-hidden ${className}`}>
      {!disableSkeleton && (
        <div className="absolute inset-0 z-20">
          <SkeletonLoader skeletonStyle={skeletonStyle} className={className} />
        </div>
      )}
    </div>
  );

  // Render image component - no background for transparent images
  const renderImage = () => {
    if (!shouldLoadMedia) {
      return renderSkeletonContainer();
    }

    if (fileNotFound) {
      return (
        <div className={`flex items-center justify-center w-full h-full bg-gray-100 border-2 border-dashed border-gray-300 rounded ${className}`}>
          <div className="flex flex-col items-center space-y-2 text-center p-4">
            {renderFileIcon()}
            <span className="text-sm font-medium text-gray-600">File not found</span>
            <span className="text-xs text-gray-500">The requested file does not exist in storage</span>
          </div>
        </div>
      );
    }

    if (imageError) {
      return (
        <div className="flex items-center justify-center w-full h-full bg-gray-100 text-gray-400 p-4 text-center">
          <div>
            <FileIconRenderer fileCategory="image" className="h-8 w-8 mx-auto mb-2" />
            <span className="text-xs block mb-1">Error loading image</span>
            <span className="text-xs text-gray-500 block">
              Try refreshing with Ctrl+Shift+R
            </span>
          </div>
        </div>
      );
    }

    // Determine objectFit based on aspect ratio
    // If aspect ratio is detected and NOT exactly 9:16 (0.5625) or 16:9 (1.777...), use cover
    // Otherwise use the style's objectFit or default to contain
    const validObjectFitValues = ['contain', 'cover', 'fill', 'none', 'scale-down'] as const;
    type ValidObjectFit = typeof validObjectFitValues[number];

    let objectFit: ValidObjectFit;
    if (style?.objectFit && validObjectFitValues.includes(style.objectFit as ValidObjectFit)) {
      // Explicitly set with valid value, use it
      objectFit = style.objectFit as ValidObjectFit;
    } else if (aspectRatio !== null) {
      // Check if aspect ratio is exactly 9:16 (0.5625) or 16:9 (1.777...)
      const is9to16 = Math.abs(aspectRatio - 0.5625) < 0.01; // Allow small floating point differences
      const is16to9 = Math.abs(aspectRatio - 1.7777777777777777) < 0.01;
      objectFit = (is9to16 || is16to9) ? 'contain' : 'cover';
    } else {
      // No aspect ratio detected yet, default to cover for better visual experience
      objectFit = 'cover';
    }

    // Remove object-contain/object-cover from className if style has objectFit
    const imageClassName = className.replace(/object-\w+/g, '').trim();

    // Special handling for common image formats - try direct loading first
    const srcLower = (typeof effectiveSrc === 'string' ? effectiveSrc : '').toLowerCase();
    const isCommonImageFormat = srcLower.includes('.png') ||
      srcLower.includes('.jpg') ||
      srcLower.includes('.jpeg') ||
      srcLower.includes('.webp') ||
      srcLower.includes('.svg');

    // Check if URL has query strings (Next.js Image doesn't support query strings)
    const hasQueryString = (typeof effectiveSrc === 'string' && effectiveSrc.includes('?')) || (normalizedFinalUrl?.includes('?') ?? false);

    if (isCommonImageFormat) {
      // Use regular img tag if URL has query strings (Next.js Image restriction) or if forceImgTag is true
      if (hasQueryString || forceImgTag) {
        return (
          <div className="relative w-full h-full overflow-hidden">
            {/* PERFORMANCE: Add explicit dimensions and fetchpriority for LCP optimization */}
            <img
              src={(normalizedFinalUrl || effectiveSrc) ?? ''}
              alt={alt}
              width={1920}
              height={1080}
              className={`w-full h-full transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'} ${imageClassName}`}
              style={{ ...style, objectFit }}
              onLoad={handleImageLoad}
              onError={handleImageError}
              loading={isPriorityLoad ? "eager" : "lazy"}
              decoding={isPriorityLoad ? "sync" : "async"}
              fetchPriority={isPriorityLoad ? "high" : "auto"}
            />
            {isLoading && !disableSkeleton && (
              <div className="absolute inset-0 z-20">
                <SkeletonLoader skeletonStyle={skeletonStyle} className={className} />
              </div>
            )}
          </div>
        );
      }

      // Use Next.js Image for URLs without query strings
      return (
        <div className="relative w-full h-full overflow-hidden">
            <Image
            src={effectiveSrc} // Use effective URL directly for common image formats
            alt={alt}
            fill
            sizes={isPriorityLoad 
              ? "100vw" // Hero images: full viewport width
              : "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            }
            className={`w-full h-full transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'} ${imageClassName}`}
            style={{ ...style, objectFit }}
            onLoad={handleImageLoad}
            onError={(e) => {
              // If direct load fails, try the API route
              const target = e.currentTarget as HTMLImageElement;
              if (normalizedFinalUrl) {
                target.src = normalizedFinalUrl;
              }
            }}
            priority={isPriorityLoad}
            {...(isPriorityLoad ? {} : { loading: 'lazy' })}
          />
          {isLoading && (
            <div className="absolute inset-0 z-20">
              <SkeletonLoader />
            </div>
          )}
        </div>
      );
    }

    // For SVG files served through our API, use regular img tag if forceImgTag is true or has query string
    if (safeIsS3Url && safeS3Key && (normalizedFinalUrl?.includes('.svg') || (typeof effectiveSrc === 'string' && effectiveSrc.includes('.svg')))) {
      if (forceImgTag || hasQueryString) {
        return (
          <div className="relative w-full h-full overflow-hidden">
            <img
              src={normalizedFinalUrl ?? ''}
              alt={alt}
              width={800}
              height={600}
              className={`w-full h-full transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'} ${imageClassName}`}
              style={{ ...style, objectFit }}
              onLoad={handleImageLoad}
              onError={handleImageError}
              loading={isPriorityLoad ? "eager" : "lazy"}
              decoding={isPriorityLoad ? "sync" : "async"}
            />
            {isLoading && !disableSkeleton && (
              <div className="absolute inset-0 z-20">
                <SkeletonLoader skeletonStyle={skeletonStyle} className={className} />
              </div>
            )}
          </div>
        );
      }
      return (
        <div className="relative w-full h-full overflow-hidden">
          <Image
            src={(normalizedFinalUrl ?? effectiveSrc) || ''}
            alt={alt}
            width={1000}
            height={1000}
            className={`w-full h-full transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'} ${imageClassName}`}
            style={{ ...style, objectFit }}
            onLoad={handleImageLoad}
            onError={handleImageError}
            priority={isPriorityLoad}
            {...(isPriorityLoad ? {} : { loading: 'lazy' })}
          />
          {isLoading && (
            <div className="absolute inset-0 z-20">
              <SkeletonLoader />
            </div>
          )}
        </div>
      );
    }

    // For all other S3 files served through our API, check if URL has query strings
    if (safeIsS3Url && safeS3Key) {
      // Check if URL has query strings (like /api/v1/media/download?key=...&view=true)
      const hasQueryStringS3 = (normalizedFinalUrl?.includes('?') ?? false) || (typeof effectiveSrc === 'string' && effectiveSrc.includes('?'));

      // Use regular img tag for API routes with query strings or if forceImgTag is true
      if (hasQueryStringS3 || forceImgTag) {
        return (
          <div className="relative w-full h-full overflow-hidden">
            <img
              ref={imageRef}
              src={normalizedFinalUrl ?? ''}
              alt={alt}
              width={1920}
              height={1080}
              className={`w-full h-full transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'} ${imageClassName}`}
              style={{ ...style, objectFit }}
              onLoad={handleImageLoad}
              onError={handleImageError}
              loading={isPriorityLoad ? "eager" : "lazy"}
              decoding={isPriorityLoad ? "sync" : "async"}
              fetchPriority={isPriorityLoad ? "high" : "auto"}
            />
            {isLoading && !disableSkeleton && (
              <div className="absolute inset-0 z-20">
                <SkeletonLoader skeletonStyle={skeletonStyle} className={className} />
              </div>
            )}
          </div>
        );
      }

      // Use Next.js Image for S3 URLs without query strings
      return (
        <div className="relative w-full h-full overflow-hidden">
          <Image
            src={normalizedFinalUrl ?? effectiveSrc}
            alt={alt}
            className={`w-full h-full transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'} ${imageClassName}`}
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            style={{ ...style, objectFit }}
            onLoad={handleImageLoad}
            onError={handleImageError}
            priority={isPriorityLoad}
            {...(isPriorityLoad ? {} : { loading: 'lazy' })}
          />
          {isLoading && (
            <div className="absolute inset-0 z-20">
              <SkeletonLoader />
            </div>
          )}
        </div>
      );
    }

    // For external images (non-S3), check if we should force img tag
    if (forceImgTag || hasQueryString) {
      return (
        <div className="relative w-full h-full overflow-hidden">
          {/* PERFORMANCE: Added explicit dimensions to prevent CLS */}
          <img
            ref={imageRef}
            src={normalizedFinalUrl || effectiveSrc}
            alt={alt}
            width={1920}
            height={1080}
            className={`w-full h-full transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'} ${imageClassName}`}
            style={{ ...style, objectFit }}
            onLoad={handleImageLoad}
            onError={handleImageError}
            loading={isPriorityLoad ? "eager" : "lazy"}
            decoding={isPriorityLoad ? "sync" : "async"}
          />
          {isLoading && !disableSkeleton && (
            <div className="absolute inset-0 z-20">
              <SkeletonLoader />
            </div>
          )}
        </div>
      );
    }

    // For external images (non-S3), use Next.js Image component
    return (
      <div className="relative w-full h-full overflow-hidden">
        <Image
          src={normalizedFinalUrl || effectiveSrc}
          alt={alt}
          fill
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          className={`w-full h-full transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'} ${imageClassName}`}
          style={{ ...style, objectFit }}
          onLoad={handleImageLoad}
          onError={handleImageError}
          priority={isPriorityLoad}
          {...(isPriorityLoad ? {} : { loading: 'lazy' })}
        />
        {isLoading && (
          <div className="absolute inset-0 z-20">
            <SkeletonLoader />
          </div>
        )}
      </div>
    );
  };

  const handleContainerClick = (event: React.MouseEvent<HTMLDivElement>) => {
    onClick?.(event);
    if (onClick) {
      return;
    }
    if (!shouldEnablePreviewModal) {
      return;
    }
    // For videos, use the video modal context
    // Extract objectPosition from style prop if available
    const objectPosition = style?.objectPosition as string | undefined;
    if (isVideo && !shouldAutoplay) {
      openVideoModal(normalizedFinalUrl || effectiveSrc, alt, objectPosition);
    } else if (isImage) {
      // For images, we can still use local modal or context - keeping local for now
      // Images can use the same modal system if needed
      openVideoModal(normalizedFinalUrl || effectiveSrc, alt, objectPosition);
    }
  };

  // Renderizar según el tipo de archivo
  // #region agent log
  if (typeof window !== 'undefined') {
    const renderDecisionLog = {location:'S3FilePreview.tsx:1959',message:'Render decision',data:{isImage,isVideo,isPdf,imageError,fileAnalysis:{isVideo:fileAnalysis?.isVideo,isImage:fileAnalysis?.isImage,detectedFileType:fileAnalysis?.detectedFileType},effectiveSrc,normalizedFinalUrl,providedFileType,fileName},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'};
    console.log('[DEBUG]', renderDecisionLog);
  }
  // #endregion
  return (
    <>
      <div ref={containerRef} className="relative group" style={style} onClick={handleContainerClick}>
        {isImage && !imageError ? (
          // All images (including SVGs) - render as image
          renderImage()
        ) : isPdf ? (
          // Para PDFs, mostrar un icono de PDF con opción para ver
          <div className={`flex flex-col items-center justify-center ${className}`}>
            <FileIconRenderer fileCategory="pdf" className="h-12 w-12" />
            <span className="text-xs mt-1 text-center truncate max-w-full">
              {displayFileName}
            </span>
            <a
              href={normalizedFinalUrl ?? undefined}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-500 hover:underline mt-1"
              onClick={(e) => e.stopPropagation()}
            >
              Ver PDF
            </a>
          </div>
        ) : isVideo ? (
          // Para videos, usar un tag de video
          <div ref={videoContainerRef} className="relative w-full h-full overflow-hidden">
            {!shouldLoadMedia ? (
              renderSkeletonContainer()
            ) : fileNotFound ? (
              <div className="flex items-center justify-center w-full h-full bg-gray-100 border-2 border-dashed border-gray-300 rounded">
                <div className="flex flex-col items-center space-y-2 text-center p-4">
                  {renderFileIcon()}
                  <span className="text-sm font-medium text-gray-600">File not found</span>
                  <span className="text-xs text-gray-500">The requested file does not exist in storage</span>
                </div>
              </div>
            ) : videoError ? (
              // Show fallback poster image when video fails to load
              <div className="w-full h-full">
                {resolvedPoster || generatedPosterUrl ? (
                  <img
                    src={resolvedPoster || generatedPosterUrl || ''}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="eager"
                    style={{ objectFit: style?.objectFit || computedObjectFit || 'cover' }}
                  />
                ) : (
                  <div className="flex items-center justify-center w-full h-full bg-gray-100 text-gray-400 p-4 text-center">
                    <div>
                      <FileIconRenderer fileCategory="video" className="h-8 w-8 mx-auto mb-2" />
                      <span className="text-xs block mb-1">Error loading video</span>
                      <span className="text-xs text-gray-500 block">
                        {typeof normalizedFinalUrl === 'string' ? normalizedFinalUrl : (typeof effectiveSrc === 'string' ? effectiveSrc : '')}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ) : !controls ? (
              // CRITICAL: For CRITICAL priority videos, always show fallback image first
              // Video element loads in background (hidden), then shows when both page AND video are fully loaded
              <>
                {/* Fallback image - always shown for CRITICAL videos until video is ready */}
                {isPriorityLoad && videoPoster && !shouldShowVideo && (
                  <img
                    src={videoPoster}
                    alt={alt}
                    className={`absolute inset-0 z-10 ${className || "w-full h-full object-cover"}`}
                    style={style}
                    loading="eager"
                    fetchPriority="high"
                    onLoad={onLoad}
                    onError={onError}
                  />
                )}
                {/* Video element - always rendered for CRITICAL videos (hidden until ready) */}
                {/* For non-CRITICAL videos, use old isSlow logic */}
                {(!isPriorityLoad && !shouldShowVideo && videoPoster) ? (
                <img
                  src={videoPoster}
                  alt={alt}
                  className={className || "w-full h-full object-cover"}
                  style={style}
                  loading="eager"
                  fetchPriority={isPriorityLoad ? 'high' : 'auto'}
                  onLoad={onLoad}
                  onError={onError}
                />
              ) : (
                // When controls are false, show video element with poster (for playback capability)
                // The video element must be visible (not hidden) so it can be played
                // Ensure it always shows something visible (poster or first frame)
                <video
                ref={videoRef}
                src={(() => {
                // Check during render if another component is already handling this video
                // This must be checked during render, not in useEffect, to prevent race conditions
                const isAlreadyHandled = normalizedFinalUrl && fileAnalysis?.isVideo 
                  ? isVideoAlreadyHandled(normalizedFinalUrl) 
                  : false;
                
                // Skip setting src if another component is already handling this video
                if (isAlreadyHandled && normalizedFinalUrl) {
                  // IMPORTANT: never unset src if this instance already has one.
                  // Returning undefined here can reset the media element and leave a black frame with controls.
                  const existingSrcAttr = videoRef.current?.getAttribute('src');
                  const existingSrc = existingSrcAttr || programmaticSrcRef.current;
                  if (!existingSrc) {
                    return undefined; // First render while another instance handles loading
                  }
                }
                
                // Mark as loading during render to prevent other components from loading it
                // This must happen BEFORE we return the src, so other components see it as "handled"
                if (normalizedFinalUrl && fileAnalysis?.isVideo && !isAlreadyHandled) {
                  // Check if we've already marked it (to avoid multiple marks)
                  const entry = globalVideoLoadTracker.get(normalizedFinalUrl);
                  if (!entry || !entry.loading) {
                    markVideoLoading(normalizedFinalUrl);
                  }
                }
                
                const finalSrc = (shouldSetVideoSrc || programmaticSrcRef.current)
                  ? (normalizedFinalUrl || programmaticSrcRef.current || undefined)
                  : undefined;
                // #region agent log
                if (typeof window !== 'undefined' && fileAnalysis?.isVideo) {
                  const logData = {location:'S3FilePreview.tsx:2034',message:'Video src assignment',data:{shouldSetVideoSrc,programmaticSrcRef:programmaticSrcRef.current,normalizedFinalUrl,finalSrc,isInView,isPriorityLoad,effectivePriority,videoPoster,generatedPosterUrl,hasPoster:!!videoPoster},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'};
                  console.log('[DEBUG]', logData);
                }
                // #endregion
                // Use useEffect to verify src is set after render
                return finalSrc;
              })()}
                controls={false}
                autoPlay={shouldAutoplay}
                loop={loop}
                muted={shouldAutoplay ? true : muted}
                playsInline={shouldAutoplay ? true : playsInline}
                preload={effectivePreload}
                poster={videoPoster || generatedPosterUrl || undefined}
                onLoadStart={() => {
                  // #region agent log
                  const logData = {location:'S3FilePreview.tsx:2048',message:'Video onLoadStart',data:{normalizedFinalUrl,readyState:videoRef.current?.readyState},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'};
                  console.log('[DEBUG]', logData);
                  // #endregion
                }}
                crossOrigin={shouldAutoplay ? 'anonymous' : undefined}
                // OPTIMIZED: Add fetchpriority for CRITICAL/HIGH priority videos
                {...(isPriorityLoad ? { fetchPriority: 'high' as const } : {})}
                className={className || "w-full h-full"}
                style={{
                  ...style,
                  objectFit: style?.objectFit || computedObjectFit || 'cover',
                  objectPosition: style?.objectPosition || 'center',
                  backgroundColor: '#000', // Ensure background is visible while loading
                  // CRITICAL: Hide video until both page and video are fully loaded
                  opacity: isPriorityLoad && !shouldShowVideo ? 0 : 1,
                  zIndex: isPriorityLoad && !shouldShowVideo ? 0 : 10,
                }}
                onClick={(e) => {
                  if (onClick) {
                    e.stopPropagation();
                    onClick(e as unknown as React.MouseEvent<HTMLDivElement, MouseEvent>);
                  }
                }}
                onLoadedMetadata={() => {
                  setIsLoading(false);
                  setVideoError(false);
                  // #region agent log
                  const logData = {location:'S3FilePreview.tsx:2070',message:'Video onLoadedMetadata',data:{normalizedFinalUrl,readyState:videoRef.current?.readyState,videoWidth:videoRef.current?.videoWidth,videoHeight:videoRef.current?.videoHeight,hasPoster:!!videoPoster},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'};
                  console.log('[DEBUG]', logData);
                  // #endregion

                  // Calculate video aspect ratio
                  if (videoRef.current && videoRef.current.videoWidth && videoRef.current.videoHeight) {
                    const ratio = videoRef.current.videoWidth / videoRef.current.videoHeight;
                    setAspectRatio(ratio);

                    // Determine objectFit based on aspect ratio
                    const is9to16 = Math.abs(ratio - 0.5625) < 0.01;
                    const is16to9 = Math.abs(ratio - 1.7777777777777777) < 0.01;
                    setComputedObjectFit((is9to16 || is16to9) ? 'contain' : 'cover');
                  }
                  
                  // Check if video is ready for playback
                  if (videoRef.current && videoRef.current.readyState >= 4) {
                    setVideoReadyForPlayback(true);
                  }
                  // Note: Don't seek to 0 here - poster generation useEffect handles seeking to 0.5s
                }}
                onLoadedData={(e) => {
                  setIsLoading(false);
                  setVideoError(false);
                  const video = e.currentTarget;
                  setVideoReadyState(video.readyState);
                  // Note: Don't seek here - poster generation useEffect handles seeking to 0.5s
                }}
                onCanPlay={(e) => {
                  setIsLoading(false);
                  setVideoError(false);
                  const video = e.currentTarget;
                  setVideoReadyState(video.readyState);
                  // #region agent log
                  const logData = {location:'S3FilePreview.tsx:2098',message:'Video onCanPlay',data:{normalizedFinalUrl,readyState:video.readyState,hasPoster:!!videoPoster},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'};
                  console.log('[DEBUG]', logData);
                  // #endregion
                  
                  // Check if video is ready for playback
                  if (video.readyState >= 4) {
                    setVideoReadyForPlayback(true);
                    // CRITICAL FIX: Explicitly trigger playback when video is ready and autoplay is enabled
                    // This ensures the poster hides even if native autoplay attribute fails
                    // Use requestIdleCallback to defer playback until idle time (reduces TBT)
                    if (shouldAutoplay && video.paused && !autoplayBlocked) {
                      video.muted = true;
                      const playVideo = () => {
                        if ('requestIdleCallback' in window) {
                          requestIdleCallback(() => {
                            video.play().then(() => {
                              setHasPlayed(true);
                              setAutoplayBlocked(false);
                              // #region agent log
                              const playLogData = {location:'S3FilePreview.tsx:2125',message:'Video play() succeeded',data:{normalizedFinalUrl},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'};
                              console.log('[DEBUG]', playLogData);
                              // #endregion
                            }).catch((error) => {
                              setAutoplayBlocked(true);
                              // #region agent log
                              const playErrorLogData = {location:'S3FilePreview.tsx:2132',message:'Video play() failed',data:{normalizedFinalUrl,error:error.message},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'};
                              console.log('[DEBUG]', playErrorLogData);
                              // #endregion
                            });
                          }, { timeout: 1000 });
                        } else {
                          setTimeout(() => {
                            video.play().then(() => {
                              setHasPlayed(true);
                              setAutoplayBlocked(false);
                            }).catch((error) => {
                              setAutoplayBlocked(true);
                            });
                          }, 100);
                        }
                      };
                      playVideo();
                    }
                  }
                  // Note: Don't seek here - poster generation useEffect handles seeking to 0.5s
                }}
                onCanPlayThrough={(e) => {
                  // Video has enough data to play through without stopping
                  setVideoReadyForPlayback(true);
                  setIsLoading(false);
                  setVideoError(false);
                  const video = e.currentTarget;
                  setVideoReadyState(video.readyState);
                  // CRITICAL FIX: Explicitly trigger playback when video is ready and autoplay is enabled
                  // This ensures the poster hides even if native autoplay attribute fails
                  // Use requestIdleCallback to defer playback until idle time (reduces TBT)
                  if (shouldAutoplay && video.paused && !autoplayBlocked) {
                    video.muted = true;
                    if ('requestIdleCallback' in window) {
                      requestIdleCallback(() => {
                        video.play().then(() => {
                          setHasPlayed(true);
                          setAutoplayBlocked(false);
                        }).catch(() => {
                          setAutoplayBlocked(true);
                        });
                      }, { timeout: 1000 });
                    } else {
                      setTimeout(() => {
                        video.play().then(() => {
                          setHasPlayed(true);
                          setAutoplayBlocked(false);
                        }).catch(() => {
                          setAutoplayBlocked(true);
                        });
                      }, 100);
                    }
                  }
                }}
                onProgress={(e) => {
                  // Update readyState as video loads more data
                  const video = e.currentTarget;
                  if (video.readyState >= 2) {
                    // HAVE_CURRENT_DATA - video has enough data to show a frame
                    setVideoReadyState(video.readyState);
                    setIsLoading(false);
                  }
                }}
                onSeeked={() => {
                  // Update poster if we have a generated one
                  if (videoRef.current && generatedPosterUrl) {
                    videoRef.current.poster = generatedPosterUrl;
                  }
                }}
                onError={async (e) => {
                  setIsLoading(false);
                  // #region agent log
                  const logData = {location:'S3FilePreview.tsx:2126',message:'Video error event',data:{normalizedFinalUrl,error:e.currentTarget.error,readyState:e.currentTarget.readyState},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'};
                  console.log('[DEBUG]', logData);
                  // #endregion

                  // Check if it's a 404 error or CORS error by trying to fetch the URL
                  if (normalizedFinalUrl && normalizedFinalUrl.includes('/api/media/download')) {
                    try {
                      const response = await fetch(normalizedFinalUrl, { method: 'HEAD' });
                      if (response.status === 404) {
                        setFileNotFound(true);
                        setVideoError(true);
                        // CACHE FIX: Invalidate cache for deleted S3 files
                        await mediaCache.invalidateUrl(normalizedFinalUrl);
                      } else if (!corsRetryAttempted && !normalizedFinalUrl.includes('nocdn=true') && shouldRetryWithNoCdn(response.status)) {
                        console.warn('[S3FilePreview] CDN returned error status - retrying with direct API access (nocdn=true)', {
                          url: normalizedFinalUrl,
                          status: response.status
                        });
                        setCorsRetryAttempted(true);
                        return;
                      } else {
                        // Not a 404 - might be a temporary network issue, retry loading
                        if (videoRef.current && !videoRef.current.error) {
                          // Retry loading the video source
                          const currentSrc = videoRef.current.src;
                          videoRef.current.load();
                          // If load() doesn't work, try reloading the source
                          setTimeout(() => {
                            if (videoRef.current && videoRef.current.error) {
                              videoRef.current.src = '';
                              videoRef.current.src = currentSrc;
                              videoRef.current.load();
                            }
                          }, 1000);
                        } else {
                          setVideoError(true);
                        }
                      }
                    } catch (fetchError) {
                      // If fetch fails, might be CORS issue - retry with nocdn=true
                      // This bypasses CloudFront and serves directly from S3
                      if (!corsRetryAttempted && normalizedFinalUrl.includes('/api/media/download') && !normalizedFinalUrl.includes('nocdn=true')) {
                        console.warn('[S3FilePreview] CORS/CloudFront error detected - retrying with direct API access (nocdn=true)', {
                          url: normalizedFinalUrl,
                          error: fetchError instanceof Error ? fetchError.message : String(fetchError)
                        });
                        setCorsRetryAttempted(true);
                        // The normalizedFinalUrl will be recalculated with nocdn=true
                        // and the video will reload automatically due to state change
                        return;
                      }
                      // If already retried or not applicable, set error
                      if (videoRef.current) {
                        const currentSrc = videoRef.current.src;
                        videoRef.current.load();
                        setTimeout(() => {
                          if (videoRef.current && videoRef.current.error) {
                            videoRef.current.src = '';
                            videoRef.current.src = currentSrc;
                            videoRef.current.load();
                          }
                        }, 1000);
                      } else {
                        setVideoError(true);
                      }
                    }
                  } else {
                    setVideoError(true);
                  }

                  if (onError) {
                    onError();
                  }
                  if (process.env.NODE_ENV === 'development') {
                    console.warn('[S3FilePreview] Video load error for:', src);
                    console.warn('- Final URL:', normalizedFinalUrl);
                    console.warn('- S3 Key:', safeS3Key);
                    console.warn('- Video element:', e.currentTarget);
                  }
                }}
              >
                <track kind="captions" />
                Tu navegador no soporta el tag de video.
              </video>
              )
              }
              </>
            ) : (
              // CRITICAL: For CRITICAL priority videos, always show fallback image first
              // Video element loads in background (hidden), then shows when both page AND video are fully loaded
              <>
                {/* Fallback image - always shown for CRITICAL videos until video is ready */}
                {isPriorityLoad && videoPoster && !shouldShowVideo && (
                  <img
                    src={videoPoster}
                    alt={alt}
                    className={`absolute inset-0 z-10 ${className || "w-full h-full object-cover"}`}
                    style={style}
                    loading="eager"
                    fetchPriority="high"
                    onLoad={onLoad}
                    onError={onError}
                  />
                )}
                {/* Video element - always rendered for CRITICAL videos (hidden until ready) */}
                {/* For non-CRITICAL videos, use old isSlow logic */}
                {(!isPriorityLoad && !shouldShowVideo && videoPoster) ? (
                <img
                  src={videoPoster}
                  alt={alt}
                  className={className || "w-full h-full object-cover"}
                  style={style}
                  loading="eager"
                  fetchPriority={isPriorityLoad ? 'high' : 'auto'}
                  onLoad={onLoad}
                  onError={onError}
                />
              ) : (
              // When controls is true, show video with controls
              <video
                ref={videoRef}
                src={(() => {
                  // Check during render if another component is already handling this video
                  const videoUrl = typeof normalizedFinalUrl === 'string' ? normalizedFinalUrl : effectiveSrc;
                  const isAlreadyHandled = videoUrl && fileAnalysis?.isVideo 
                    ? isVideoAlreadyHandled(videoUrl) 
                    : false;
                  
                  // Skip setting src if another component is already handling this video
                  if (isAlreadyHandled && videoUrl) {
                    // IMPORTANT: never unset src if this instance already has one.
                    // Returning undefined here can reset the media element and leave a black frame with controls.
                    const existingSrcAttr = videoRef.current?.getAttribute('src');
                    const existingSrc = existingSrcAttr || programmaticSrcRef.current;
                    if (!existingSrc) {
                      return undefined; // First render while another instance handles loading
                    }
                  }
                  
                  // Mark as loading during render to prevent other components from loading it
                  if (videoUrl && fileAnalysis?.isVideo && !isAlreadyHandled) {
                    const entry = globalVideoLoadTracker.get(videoUrl);
                    if (!entry || !entry.loading) {
                      markVideoLoading(videoUrl);
                    }
                  }
                  
                  const finalSrc = ((shouldSetVideoSrc || programmaticSrcRef.current)
                    ? (videoUrl || programmaticSrcRef.current || undefined)
                    : undefined) as string | undefined;
                  return finalSrc;
                })()}
                controls={shouldAutoplay ? false : controls}
                autoPlay={shouldAutoplay}
                loop={loop}
                muted={shouldAutoplay ? true : muted}
                playsInline={shouldAutoplay ? true : playsInline}
                preload={effectivePreload}
                poster={videoPoster || undefined}
                crossOrigin={shouldAutoplay ? 'anonymous' : undefined}
                // OPTIMIZED: Add fetchpriority for CRITICAL/HIGH priority videos
                {...(isPriorityLoad ? { fetchPriority: 'high' as const } : {})}
                className={className || "w-full h-full"}
                style={{
                  ...style,
                  objectFit: style?.objectFit || computedObjectFit,
                  objectPosition: style?.objectPosition || 'center',
                  // OPTIMIZATION: Explicit aspect ratio to prevent CLS (Cumulative Layout Shift)
                  // Use aspectRatio from metadata if available, otherwise default to 16:9
                  aspectRatio: aspectRatio !== null 
                    ? aspectRatio 
                    : (style?.aspectRatio || '16 / 9'),
                  // CRITICAL: Hide video until both page and video are fully loaded
                  opacity: isPriorityLoad && !shouldShowVideo ? 0 : 1,
                  zIndex: isPriorityLoad && !shouldShowVideo ? 0 : 10,
                }}
                onClick={(e) => {
                  if (onClick) {
                    e.stopPropagation();
                    onClick(e as unknown as React.MouseEvent<HTMLDivElement, MouseEvent>);
                  }
                  // Trigger play on click for mobile autoplay (immediate on mobile, deferred on desktop)
                  if (shouldAutoplay && videoRef.current) {
                    videoRef.current.muted = true;
                    playVideoIdle(videoRef.current, undefined, isMobile);
                  }
                }}
                onLoadedMetadata={(e) => {
                  setIsLoading(false);
                  setVideoError(false);
                  const video = e.currentTarget;
                  setVideoReadyState(video.readyState);

                  // Calculate video aspect ratio
                  if (videoRef.current && videoRef.current.videoWidth && videoRef.current.videoHeight) {
                    const ratio = videoRef.current.videoWidth / videoRef.current.videoHeight;
                    setAspectRatio(ratio);

                    // Determine objectFit based on aspect ratio
                    const is9to16 = Math.abs(ratio - 0.5625) < 0.01;
                    const is16to9 = Math.abs(ratio - 1.7777777777777777) < 0.01;
                    setComputedObjectFit((is9to16 || is16to9) ? 'contain' : 'cover');
                  }

                  // Try to play video if autoplay is enabled (for background videos)
                  // On mobile: play immediately, on desktop: defer with requestIdleCallback
                  if (shouldAutoplay && videoRef.current) {
                    videoRef.current.muted = true;
                    playVideoIdle(videoRef.current, (error) => {
                      if (process.env.NODE_ENV === 'development') {
                        console.warn('[S3FilePreview] Video autoplay failed:', error);
                      }
                    }, isMobile);
                  }
                }}
                onLoadedData={(e) => {
                  // Fallback: also set loading to false when data is loaded
                  setIsLoading(false);
                  setVideoError(false);
                  const video = e.currentTarget;
                  setVideoReadyState(video.readyState);
                  // Try to play video if autoplay is enabled (immediate on mobile, deferred on desktop)
                  if (shouldAutoplay && videoRef.current) {
                    videoRef.current.muted = true;
                    playVideoIdle(videoRef.current, undefined, isMobile);
                  }
                }}
                onCanPlay={(e) => {
                  // Fallback: set loading to false when video can play
                  setIsLoading(false);
                  setVideoError(false);
                  const video = e.currentTarget;
                  setVideoReadyState(video.readyState);
                  // Try to play video if autoplay is enabled (for background videos)
                  // On mobile: play immediately, on desktop: defer with requestIdleCallback
                  if (shouldAutoplay && videoRef.current) {
                    videoRef.current.muted = true;
                    playVideoIdle(videoRef.current, (error) => {
                      if (process.env.NODE_ENV === 'development') {
                        console.warn('[S3FilePreview] Video autoplay failed:', error);
                      }
                    }, isMobile);
                  }
                }}
                onProgress={(e) => {
                  // Update readyState as video loads more data - hide fallback as soon as video has frame data
                  const video = e.currentTarget;
                  if (video.readyState >= 2) {
                    // HAVE_CURRENT_DATA - video has enough data to show a frame
                    setVideoReadyState(video.readyState);
                    setIsLoading(false);
                  }
                }}
                onTouchStart={() => {
                  // Trigger play on touch for mobile autoplay (immediate on mobile, deferred on desktop)
                  if (shouldAutoplay && videoRef.current) {
                    videoRef.current.muted = true;
                    playVideoIdle(videoRef.current, undefined, isMobile);
                  }
                }}
                onError={async (e) => {
                  setIsLoading(false);

                  // Check if it's a 404 error or CORS error by trying to fetch the URL
                  if (normalizedFinalUrl && typeof normalizedFinalUrl === 'string' && normalizedFinalUrl.includes('/api/media/download')) {
                    try {
                      const response = await fetch(normalizedFinalUrl, { method: 'HEAD' });
                      if (response.status === 404) {
                        setFileNotFound(true);
                        setVideoError(true);
                        // CACHE FIX: Invalidate cache for deleted S3 files
                        await mediaCache.invalidateUrl(normalizedFinalUrl);
                      } else if (!corsRetryAttempted && !normalizedFinalUrl.includes('nocdn=true') && shouldRetryWithNoCdn(response.status)) {
                        console.warn('[S3FilePreview] CDN returned error status - retrying with direct API access (nocdn=true)', {
                          url: normalizedFinalUrl,
                          status: response.status
                        });
                        setCorsRetryAttempted(true);
                        return;
                      }
                    } catch (fetchError) {
                      // If fetch fails, might be CORS issue - retry with nocdn=true
                      if (!corsRetryAttempted && normalizedFinalUrl.includes('/api/media/download') && !normalizedFinalUrl.includes('nocdn=true')) {
                        console.warn('[S3FilePreview] CORS/CloudFront error detected - retrying with direct API access (nocdn=true)', {
                          url: normalizedFinalUrl,
                          error: fetchError instanceof Error ? fetchError.message : String(fetchError)
                        });
                        setCorsRetryAttempted(true);
                        // The normalizedFinalUrl will be recalculated with nocdn=true
                        // and the video will reload automatically due to state change
                        return;
                      }
                      // If already retried or not applicable, set error
                      setFileNotFound(true);
                      setVideoError(true);
                      // Also invalidate on fetch failure
                      await mediaCache.invalidateUrl(normalizedFinalUrl);
                    }
                  } else {
                    setVideoError(true);
                  }

                  if (onError) {
                    onError();
                  }
                  if (process.env.NODE_ENV === 'development') {
                    console.warn('[S3FilePreview] Video load error for:', effectiveSrc);
                    console.warn('- Final URL:', normalizedFinalUrl);
                    console.warn('- S3 Key:', safeS3Key);
                    console.warn('- Video element:', e.currentTarget);
                  }
                }}
              >
                <track kind="captions" />
                Tu navegador no soporta el tag de video.
              </video>
              )
              }
              </>
            )}
            {/* Show generated poster as overlay - MUST always stay visible */}
            {/* CRITICAL: Poster image must never hide - it serves as LCP and ensures page always has content */}
            {generatedPosterUrl && (
              <div 
                className="absolute inset-0 z-10 transition-opacity duration-300"
                style={{ 
                  opacity: 1, // Always visible - never fade out
                  pointerEvents: 'none' // Allow clicks to pass through to video
                }}
              >
                <img
                  src={generatedPosterUrl}
                  alt=""
                  className="w-full h-full object-cover"
                  aria-hidden="true"
                  loading="eager"
                  fetchPriority={isPriorityLoad ? 'high' : 'auto'}
                  style={{ objectFit: style?.objectFit || computedObjectFit || 'cover' }}
                />
              </div>
            )}
            {isLoading && !videoError && !disableSkeleton && !videoPoster && !generatedPosterUrl && (
              <div className="absolute inset-0 z-20">
                <SkeletonLoader />
              </div>
            )}
            {/* Play button overlay for mobile when autoplay is blocked */}
            {isVideo && shouldAutoplay && autoplayBlocked && isMobile && !hasPlayed && (
              <div
                className="absolute inset-0 z-30 flex items-center justify-center cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  if (videoRef.current) {
                    videoRef.current.muted = true;
                    videoRef.current.setAttribute('muted', 'true');
                    videoRef.current.setAttribute('playsinline', 'true');
                    videoRef.current.setAttribute('webkit-playsinline', 'true');
                    videoRef.current.playsInline = true;

                    videoRef.current.play().then(() => {
                      setHasPlayed(true);
                      setAutoplayBlocked(false);
                    }).catch(() => {
                      // Silently handle errors
                    });
                  }
                }}
                style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  backdropFilter: 'blur(2px)',
                }}
              >
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
                  style={{
                    backgroundColor: 'rgba(0, 51, 153, 0.9)',
                    boxShadow: '0 0 40px rgba(0, 51, 153, 0.6)'
                  }}
                >
                  <Suspense fallback={<div className="w-10 h-10 bg-white rounded ml-1" />}>
                    <Play className="w-10 h-10 text-white ml-1" fill="white" />
                  </Suspense>
                </div>
              </div>
            )}
          </div>
        ) : (
          // Para otros archivos, mostrar un icono basado en el tipo
          <div className={`flex flex-col items-center justify-center ${className}`}>
            {renderFileIcon()}
            <span className="text-xs mt-1 text-center truncate max-w-full">
              {displayFileName}
            </span>
          </div>
        )}
      </div>
      {/* Modal is now rendered at PageRenderer level via VideoModal component */}
    </>
  );
};

export default S3FilePreview;
